import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TeamMemberArtifactDto, TeamMemberMediaService } from '../services/team-member-media.service';
import {
  GameThemeCatalogService,
  GameThemeArtifactReferenceUpsertDto,
  GameThemeCategoryDto,
  GameThemeCreateDto,
  GameThemeDto,
  GameThemeUpdateDto,
  DisplayPropertiesDto,
  DisplayPropertyValuesDto,
  DisplayScopeKey,
  createEmptyDisplayProperties
} from '../services/game-theme-catalog.service';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { ThemePreviewComponent } from './theme-preview.component';
import {
  ThemeVisualAssignmentsDraft,
  ThemeVisualBackgroundKey,
  ThemeVisualBlockKey,
  ThemeVisualBlockKeys,
  ThemeVisualBackgroundSlots,
  ThemeVisualSlots,
  createEmptyThemeVisualAssignments,
  isAllowedThemeVisualArtifact,
  isSafeSvgContent,
  validateThemeVisualFile
} from '../services/theme-visuals';
import {
  getThemeVariableDefaults,
  colorPickerValue,
  colorTextColor,
  formatPickedColor,
  PortalThemeKey,
  THEME_VARIABLE_DEFINITIONS,
  validateThemeVariables
} from '../services/theme-variables';
import { ThemePreviewKind, ThemePreviewState, ThemePreviewVisualReference } from '../services/theme-preview.models';
import { ThemePreviewService } from '../services/theme-preview.service';

interface ThemeDraft {
  id: number;
  themeKey: string;
  categoryId: number | null;
  name: string;
  description: string;
  isEnabled: boolean;
  isSystem: boolean;
  slotAssignments: Record<string, number | null>;
  visualAssignments: ThemeVisualAssignmentsDraft;
  unmanagedReferences: GameThemeArtifactReferenceUpsertDto[];
  themeVariables: Record<string, string>;
  displayProperties: DisplayPropertiesDto;
}

type ArtifactMediaTypeFilter = 'all' | 'audio' | 'video' | 'image' | 'svg';
type ThemeEditorTab = 'control' | 'variables' | 'media' | 'images' | 'team1' | 'team2' | 'projector' | 'roundSummary' | 'endOfGameSummary';

interface ThemeReferenceSlot {
  key: string;
  mediaRole: string;
  sequenceOrder: number;
  label: string;
  mediaType?: Exclude<ArtifactMediaTypeFilter, 'all'>;
}

interface ThemeReferenceGroup {
  title: string;
  slotKeys: string[];
}

type DisplayValueState = 'values' | 'selected' | 'unselected';
type DisplayFieldPath = 'backgroundColor' | 'imageOpacityPercent' | 'border.color' | 'border.cornerStyle' | 'border.thicknessPx' | 'font.color' | 'font.sizeRem' | 'svg.source' | 'svg.sizePx' | 'svg.direction' | 'svg.color';

interface DisplayEditorObject {
  section: string;
  object: string;
  baseFields: DisplayFieldPath[];
  supportsStates: boolean;
}

interface DisplayEditorSection {
  name: string;
  label: string;
  objects: DisplayEditorObject[];
  containers: DisplayEditorContainer[];
}

interface DisplayEditorContainer {
  object: string;
  label: string;
  objects: DisplayEditorObject[];
}

interface DisplayEditorField {
  path: DisplayFieldPath;
  label: string;
  kind: 'color' | 'number' | 'range' | 'text' | 'select';
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
}

const DISPLAY_OBJECTS_BY_SCOPE: Record<DisplayScopeKey, Record<string, string[]>> = {
  team1: {
    PageSurface: ['Background'], Team1HeroContent: ['Surface', 'CompanyName', 'TeamName', 'RoundNumber', 'RoundType'],
    Team1GameStatusContent: ['Surface', 'Team1Score', 'ConnectStatus', 'LivesStatus', 'DeathStatus'],
    Team1QuestionContent: ['Surface', 'Label', 'QuestionValue'], Team1CurrentPlayersContent: ['Surface', 'Label', 'CurrentPlayer', 'TeamMemberLabel', 'Team1TeamMembers'],
    Team1StatusContent: ['Surface', 'Label', 'Message']
  },
  team2: {
    PageSurface: ['Background'], Team2HeroContent: ['Surface', 'CompanyName', 'TeamName', 'RoundNumber', 'RoundType'],
    Team2GameStatusContent: ['Surface', 'Team2Score', 'ConnectStatus', 'LivesStatus', 'DeathStatus'],
    Team2QuestionContent: ['Surface', 'Label', 'QuestionValue'], Team2CurrentPlayersContent: ['Surface', 'Label', 'CurrentPlayer', 'TeamMemberLabel', 'Team2TeamMembers'],
    Team2StatusContent: ['Surface', 'Label', 'Message']
  },
  projector: {
    PageSurface: ['Background'], HeroContent: ['Surface', 'CompanyName', 'EventName', 'TeamName', 'RoundNumber', 'RoundType'],
    TeamScores: ['Surface', 'Label', 'ConnectStatus', 'Team1Card', 'Team1CardTeamName', 'Team1CardScore', 'Team1CardCurrentPlayer', 'Team1LivesStatus', 'Team1DeathStatus', 'Team2Card', 'Team2CardTeamName', 'Team2CardScore', 'Team2CardCurrentPlayer', 'Team2LivesStatus', 'Team2DeathStatus'],
    QuestionContent: ['Surface', 'Label', 'QuestionValue'], TeamPlayersContent: ['Surface'],
    Team1CurrentPlayersContent: ['Surface', 'Label', 'Team1Players'], Team2CurrentPlayersContent: ['Surface', 'Label', 'Team2Players'], AnswersContent: ['Surface', 'Label', 'AnswerValue']
  },
  roundSummary: {
    PageSurface: ['Background'], ProjectorRoundSummary: ['Surface'], ProjectSummaryHeader: ['CompanyName', 'RoundSummary', 'RoundNumber'],
    Team1Summary: ['Surface', 'TeamName', 'TeamScore', 'TeamTotals'], Team2Summary: ['Surface', 'TeamName', 'TeamScore', 'TeamTotals'],
    QuestionContent: ['Surface', 'QuestionValue'], AnswersContent: ['Surface', 'Answers'], Team1AnswerSummary: ['Surface', 'Answers'], Team2AnswerSummary: ['Surface', 'Answers']
  },
  endOfGameSummary: {
    PageSurface: ['Background'], ProjectorRoundSummary: ['Surface'], ProjectSummaryHeader: ['CompanyName', 'EndOfGameSummary', 'TotalRounds'],
    Team1Summary: ['Surface', 'TeamName', 'WinnerGame', 'TeamTotalScore', 'Label', 'AnswerMetrics', 'WinnerPlayer'],
    Team2Summary: ['Surface', 'TeamName', 'WinnerGame', 'TeamTotalScore', 'Label', 'AnswerMetrics', 'WinnerPlayer'], GameSummary: ['Surface', 'Label', 'AnswerMetrics']
  }
};

const DISPLAY_CONTAINER_OBJECTS = new Set([
  'team1|PageSurface|Background', 'team1|Team1HeroContent|Surface', 'team1|Team1GameStatusContent|Surface',
  'team1|Team1QuestionContent|Surface', 'team1|Team1CurrentPlayersContent|Surface', 'team1|Team1StatusContent|Surface',
  'team2|PageSurface|Background', 'team2|Team2HeroContent|Surface', 'team2|Team2GameStatusContent|Surface',
  'team2|Team2QuestionContent|Surface', 'team2|Team2CurrentPlayersContent|Surface', 'team2|Team2StatusContent|Surface',
  'projector|PageSurface|Background', 'projector|HeroContent|Surface', 'projector|TeamScores|Surface',
  'projector|TeamScores|Team1Card', 'projector|TeamScores|Team2Card', 'projector|QuestionContent|Surface',
  'projector|TeamPlayersContent|Surface', 'projector|Team1CurrentPlayersContent|Surface', 'projector|Team2CurrentPlayersContent|Surface',
  'projector|AnswersContent|Surface', 'roundSummary|PageSurface|Background', 'roundSummary|ProjectorRoundSummary|Surface',
  'roundSummary|Team1Summary|Surface', 'roundSummary|Team2Summary|Surface', 'roundSummary|QuestionContent|Surface',
  'roundSummary|AnswersContent|Surface', 'roundSummary|Team1AnswerSummary|Surface', 'roundSummary|Team2AnswerSummary|Surface',
  'endOfGameSummary|PageSurface|Background', 'endOfGameSummary|ProjectorRoundSummary|Surface',
  'endOfGameSummary|Team1Summary|Surface', 'endOfGameSummary|Team2Summary|Surface', 'endOfGameSummary|GameSummary|Surface'
]);

const DISPLAY_SECTION_LABELS: Record<string, string> = {
  Team1HeroContent: 'Team 1 Hero', Team1GameStatusContent: 'Team 1 Game Status', Team1QuestionContent: 'Team 1 Question',
  Team1CurrentPlayersContent: 'Team 1 Current Players', Team1StatusContent: 'Team 1 Status',
  Team2HeroContent: 'Team 2 Hero', Team2GameStatusContent: 'Team 2 Game Status', Team2QuestionContent: 'Team 2 Question',
  Team2CurrentPlayersContent: 'Team 2 Current Players', Team2StatusContent: 'Team 2 Status',
  HeroContent: 'Projector Hero', TeamScores: 'Team Scores', QuestionContent: 'Question', TeamPlayersContent: 'Team Players',
  AnswersContent: 'Answers',
  ProjectorRoundSummary: 'End-of-Round Summary', ProjectSummaryHeader: 'Summary Header',
  Team1Summary: 'Team 1 Summary', Team2Summary: 'Team 2 Summary', Team1AnswerSummary: 'Team 1 Answers', Team2AnswerSummary: 'Team 2 Answers',
  GameSummary: 'Game Summary'
};

const DISPLAY_OBJECT_LABELS: Record<string, string> = {
  Background: 'Background Image', Surface: 'Surface', Label: 'Label', Message: 'Message', Answers: 'Answers',
  Team1Card: 'Team 1 Card', Team2Card: 'Team 2 Card',
  Team1Players: 'Team 1 Players', Team2Players: 'Team 2 Players',
  Team1TeamMembers: 'Team 1 Team Members', Team2TeamMembers: 'Team 2 Team Members',
  Team1Score: 'Team 1 Score', Team2Score: 'Team 2 Score',
  CompanyName: 'Company Name', TeamName: 'Team Name', EventName: 'Event Name',
  RoundNumber: 'Round Number', RoundType: 'Round Type', QuestionValue: 'Question', AnswerValue: 'Answer',
  CurrentPlayer: 'Current Player', TeamMemberLabel: 'Team Member Label', ConnectStatus: 'Connection Status',
  DeathStatus: 'Death Status', LivesStatus: 'Lives Status', Team1CardTeamName: 'Team 1 Card Team Name',
  Team1CardScore: 'Team 1 Card Score', Team1CardCurrentPlayer: 'Team 1 Card Current Player',
  Team2CardTeamName: 'Team 2 Card Team Name', Team2CardScore: 'Team 2 Card Score', Team2CardCurrentPlayer: 'Team 2 Card Current Player',
  Team1LivesStatus: 'Team 1 Lives Status', Team1DeathStatus: 'Team 1 Death Status',
  Team2LivesStatus: 'Team 2 Lives Status', Team2DeathStatus: 'Team 2 Death Status',
  RoundSummary: 'Round Summary', EndOfGameSummary: 'End-of-Game Summary', TotalRounds: 'Total Rounds',
  TeamScore: 'Team Score', TeamTotals: 'Team Totals', TeamTotalScore: 'Team Total Score',
  WinnerGame: 'Game Winner', WinnerPlayer: 'Winning Player', AnswerMetrics: 'Answer Metrics'
};

const DISPLAY_BASE_FIELD_OVERRIDES: Record<string, DisplayFieldPath[]> = {
  'PageSurface|Background': ['imageOpacityPercent'],
  'Team1GameStatusContent|DeathStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'Team1GameStatusContent|LivesStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'Team2GameStatusContent|DeathStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'Team2GameStatusContent|LivesStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'Team1HeroContent|Surface': ['backgroundColor', 'border.color', 'border.cornerStyle', 'border.thicknessPx'],
  'Team2HeroContent|Surface': ['backgroundColor', 'border.color', 'border.cornerStyle', 'border.thicknessPx'],
  'TeamScores|Team1DeathStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'TeamScores|Team1LivesStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'TeamScores|Team2DeathStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'TeamScores|Team2LivesStatus': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'TeamScores|Team1Card': ['border.color', 'border.cornerStyle', 'border.thicknessPx'],
  'TeamScores|Team2Card': ['border.color', 'border.cornerStyle', 'border.thicknessPx'],
  'ProjectorRoundSummary|Surface': ['backgroundColor', 'border.color', 'border.cornerStyle', 'border.thicknessPx'],
  'Team1Summary|WinnerGame': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'Team2Summary|WinnerGame': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'Team1Summary|WinnerPlayer': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source'],
  'Team2Summary|WinnerPlayer': ['svg.color', 'svg.direction', 'svg.sizePx', 'svg.source']
};

const DISPLAY_STATEFUL_OBJECTS = new Set([
  'team1|Team1HeroContent|Surface', 'team2|Team2HeroContent|Surface',
  'projector|TeamScores|Team1Card', 'projector|TeamScores|Team2Card'
]);

const DISPLAY_CONDITIONAL_FIELDS: DisplayFieldPath[] = ['backgroundColor', 'border.color'];
const DISPLAY_FIELD_DEFINITIONS: Record<DisplayFieldPath, DisplayEditorField> = {
  backgroundColor: { path: 'backgroundColor', label: 'Background color', kind: 'color' },
  imageOpacityPercent: { path: 'imageOpacityPercent', label: 'Background image opacity', kind: 'range', min: 0, max: 100, step: 1 },
  'border.color': { path: 'border.color', label: 'Border color', kind: 'color' },
  'border.cornerStyle': { path: 'border.cornerStyle', label: 'Border corner style', kind: 'select', options: ['Square', 'Rounded', 'Pill'] },
  'border.thicknessPx': { path: 'border.thicknessPx', label: 'Border thickness (px)', kind: 'number', min: 0, step: 0.1 },
  'font.color': { path: 'font.color', label: 'Font color', kind: 'color' },
  'font.sizeRem': { path: 'font.sizeRem', label: 'Font size (rem)', kind: 'number', min: 0.01, step: 0.01 },
  'svg.source': { path: 'svg.source', label: 'SVG source (URL or data URI)', kind: 'text' },
  'svg.sizePx': { path: 'svg.sizePx', label: 'SVG size (px)', kind: 'number', min: 0.01, step: 0.1 },
  'svg.direction': { path: 'svg.direction', label: 'SVG direction', kind: 'select', options: ['Horizontal', 'Vertical'] },
  'svg.color': { path: 'svg.color', label: 'SVG color', kind: 'color' }
};

interface ThemeBuilderArtifact {
  id: number;
  fileName: string;
  contentType: string;
  blobPath: string;
  artifactScope: string;
  mediaType: Exclude<ArtifactMediaTypeFilter, 'all'>;
}

const ThemeReferenceSlots: ThemeReferenceSlot[] = [
  { key: 'rightAnswer-1', mediaRole: 'RightAnswer', sequenceOrder: 1, label: 'Right Answer 1' },
  { key: 'rightAnswer-2', mediaRole: 'RightAnswer', sequenceOrder: 2, label: 'Right Answer 2' },
  { key: 'rightAnswer-3', mediaRole: 'RightAnswer', sequenceOrder: 3, label: 'Right Answer 3' },
  { key: 'wrongAnswer-1', mediaRole: 'WrongAnswer', sequenceOrder: 1, label: 'Wrong Answer 1' },
  { key: 'wrongAnswer-2', mediaRole: 'WrongAnswer', sequenceOrder: 2, label: 'Wrong Answer 2' },
  { key: 'wrongAnswer-3', mediaRole: 'WrongAnswer', sequenceOrder: 3, label: 'Wrong Answer 3' },
  { key: 'endOfRound-1', mediaRole: 'EndOfRound', sequenceOrder: 1, label: 'End of Round (1 video)' },
  { key: 'nextRound-1', mediaRole: 'NextRound', sequenceOrder: 1, label: 'Next Round (1 video)' },
  { key: 'endOfGame-1', mediaRole: 'EndOfGame', sequenceOrder: 1, label: 'End of Game (1 video)', mediaType: 'video' },
  { key: 'nextTeamTurn-1', mediaRole: 'NextTeamTurn', sequenceOrder: 1, label: 'Next Team Turn (1 video)', mediaType: 'video' },
  { key: 'buzzer-1', mediaRole: 'Buzzer', sequenceOrder: 1, label: 'Buzzer (1 audio)', mediaType: 'audio' },
  { key: 'intro-1', mediaRole: 'Intro', sequenceOrder: 1, label: 'Intro (1 video)' },
  { key: 'break-1', mediaRole: 'Break', sequenceOrder: 1, label: 'Break (1 video)' },
  { key: 'suddenDeath-1', mediaRole: 'SuddenDeath', sequenceOrder: 1, label: 'Sudden Death (1 video)', mediaType: 'video' },
  { key: 'threeThreeAndOut-1', mediaRole: 'ThreeThreeAndOut', sequenceOrder: 1, label: 'Three Three and Out (1 video)', mediaType: 'video' },
  { key: 'threeThreeSuddenDeath-1', mediaRole: 'ThreeThreeSuddenDeath', sequenceOrder: 1, label: 'Three Three Sudden Death (1 video)', mediaType: 'video' },
  { key: 'headToHead-1', mediaRole: 'HeadToHead', sequenceOrder: 1, label: 'Head to Head (1 video)', mediaType: 'video' },
  { key: 'penaltyShot-1', mediaRole: 'PenaltyShot', sequenceOrder: 1, label: 'Penalty Shot (1 video)', mediaType: 'video' },
];

const ThemeReferenceGroups: ThemeReferenceGroup[] = [
  { title: 'Right Answer (up to 3 videos)', slotKeys: ['rightAnswer-1', 'rightAnswer-2', 'rightAnswer-3'] },
  { title: 'Wrong Answer (up to 3 videos)', slotKeys: ['wrongAnswer-1', 'wrongAnswer-2', 'wrongAnswer-3'] },
  { title: 'End of Round / Next Round / End of Game', slotKeys: ['endOfRound-1', 'nextRound-1', 'endOfGame-1'] },
  { title: 'Next Team Turn / Buzzer', slotKeys: ['nextTeamTurn-1', 'buzzer-1'] },
  { title: 'Intro / Break / Sudden Death', slotKeys: ['intro-1', 'break-1', 'suddenDeath-1'] },
  { title: 'Game Flow Modes', slotKeys: ['threeThreeAndOut-1', 'threeThreeSuddenDeath-1', 'headToHead-1', 'penaltyShot-1'] }
];

const ThemeVisualRolePrefix = 'ThemeVisual:';

@Component({
  selector: 'app-theme-builder-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent, ThemePreviewComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      @if (forbidden) {
        <section class="error-section">
          <h3>Access Denied</h3>
          <p>You do not have access to Theme Builder.</p>
        </section>
      }

      @if (!forbidden) {
        <section class="page-hero">
          <div class="page-hero-inner">
            <a routerLink="/home" class="home-btn">Home</a>
            <div class="hero-top-row">
              <div class="hero-icon hero-icon--violet" aria-hidden="true">★</div>
              <app-hero-user-pill></app-hero-user-pill>
            </div>
            <h1 class="page-title">Theme Builder</h1>
             <p class="page-sub">SiteAdmin theme catalog management.</p>
           </div>
         </section>

        <section class="page-body">
          <div class="page-body-inner">
            <div class="builder-grid">
              <section class="card card--left">
                <div class="card-hd card-hd--between">
                  <span class="card-title">Artifacts (artifacts-common)</span>
                  <button class="btn btn--primary btn--plus" type="button" title="Create new theme" (click)="startCreateTheme()">+</button>
                </div>

                <div class="left-filter-grid">
                  <label class="field field--full">
                    <span>Search</span>
                    <input type="text" [(ngModel)]="artifactSearch" placeholder="Search artifacts" />
                  </label>

                  <label class="field field--full">
                    <span>Media Type</span>
                    <select [(ngModel)]="artifactTypeFilter">
                      <option value="all">All</option>
                      <option value="audio">Audio</option>
                      <option value="video">Video</option>
                      <option value="image">Image</option>
                      <option value="svg">SVG</option>
                    </select>
                  </label>
                </div>

                <div class="common-upload">
                  <label class="btn btn--ghost common-upload__label" [class.common-upload__label--busy]="isUploadingArtifact">
                    {{ isUploadingArtifact ? 'Uploading...' : 'Upload Common Artifact' }}
                    <input
                      type="file"
                      [disabled]="isUploadingArtifact"
                      (change)="onCommonArtifactFileSelected($event)" />
                  </label>
                </div>

                <div class="artifact-list">
                  @for (artifact of filteredArtifacts(); track artifact.id) {
                    <div class="artifact-row-wrap">
                      <button
                        type="button"
                        class="artifact-row"
                        [class.artifact-row--selected]="selectedArtifactId === artifact.id"
                        (click)="selectArtifact(artifact.id)"
                        draggable="true"
                        (dragstart)="onArtifactDragStart($event, artifact)">
                        <span class="artifact-main">{{ artifact.fileName }}</span>
                        <span class="artifact-sub">{{ getMediaTypeLabel(artifact.mediaType) }} · #{{ artifact.id }}</span>
                      </button>
                      <button
                        type="button"
                        class="btn btn--ghost artifact-delete-btn"
                        [disabled]="isUploadingArtifact || deletingArtifactId === artifact.id"
                        (click)="deleteCommonArtifact(artifact.id)">
                        {{ deletingArtifactId === artifact.id ? 'Deleting...' : 'Delete' }}
                      </button>
                    </div>
                  } @empty {
                    <div class="empty">No artifacts match the current filters.</div>
                  }
                </div>
              </section>

              <section class="card card--right">
                <div class="card theme-snapshot-section">
                  <div class="card-hd existing-themes-header">
                    <span class="card-title">Existing Themes</span>
                    <input type="search" [(ngModel)]="themesSearch" placeholder="Search all theme fields" aria-label="Search existing themes" />
                  </div>

                  <div class="themes-list">
                    <table class="themes-table">
                      <thead>
                        <tr>
                          <th>Theme Key</th>
                          <th>Name</th>
                          <th>Category</th>
                          <th>Enabled</th>
                          <th>System</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (theme of filteredThemes; track theme.id) {
                          <tr>
                            <td>{{ theme.themeKey }}</td>
                            <td>{{ theme.name }}</td>
                            <td>{{ theme.category }}</td>
                            <td>{{ theme.isEnabled ? 'Yes' : 'No' }}</td>
                            <td>{{ theme.isSystem ? 'Yes' : 'No' }}</td>
                            <td>
                              <button class="btn btn--ghost" type="button" (click)="selectThemeForEdit(theme.id)">Edit</button>
                            </td>
                          </tr>
                        } @empty {
                          <tr>
                          <td class="empty" colspan="6">{{ themesSearch.trim() ? 'No themes match the search.' : 'No themes found.' }}</td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="editor-wrap">
                  <div class="theme-editor-actions">
                    <span class="count-pill">{{ themes().length }} theme{{ themes().length !== 1 ? 's' : '' }}</span>
                    <div class="actions">
                      @if (!isEditMode) {
                        <button class="btn btn--primary" type="button" [disabled]="isSavingCreate" (click)="createTheme()">
                          {{ isSavingCreate ? 'Creating...' : 'Create Theme' }}
                        </button>
                      }
                      @if (isEditMode && editOpenId !== null) {
                        <button class="btn btn--primary" type="button" [disabled]="isSavingEdit" (click)="saveEdit(editOpenId)">
                          {{ isSavingEdit ? 'Saving...' : 'Save Theme' }}
                        </button>
                        <button class="btn btn--danger" type="button" [disabled]="isDeleting" (click)="deleteTheme(editOpenId)">
                          {{ isDeleting ? 'Deleting...' : 'Delete Theme' }}
                        </button>
                      }
                    </div>
                  </div>

                  <nav class="theme-editor-tabs" role="tablist" aria-label="Theme editor sections">
                    <button class="theme-editor-tab" [class.theme-editor-tab--active]="activeEditorTab === 'control'" type="button" role="tab" [attr.aria-selected]="activeEditorTab === 'control'" (click)="selectEditorTab('control')">Control</button>
                    <button class="theme-editor-tab" [class.theme-editor-tab--active]="activeEditorTab === 'variables'" type="button" role="tab" [attr.aria-selected]="activeEditorTab === 'variables'" (click)="selectEditorTab('variables')">Variables</button>
                    <button class="theme-editor-tab" [class.theme-editor-tab--active]="activeEditorTab === 'media'" type="button" role="tab" [attr.aria-selected]="activeEditorTab === 'media'" (click)="selectEditorTab('media')">Media</button>
                    <button class="theme-editor-tab" [class.theme-editor-tab--active]="activeEditorTab === 'images'" type="button" role="tab" [attr.aria-selected]="activeEditorTab === 'images'" (click)="selectEditorTab('images')">Images</button>
                    <button class="theme-editor-tab" [class.theme-editor-tab--active]="activeEditorTab === 'team1'" type="button" role="tab" [attr.aria-selected]="activeEditorTab === 'team1'" (click)="selectEditorTab('team1')">Team 1</button>
                    <button class="theme-editor-tab" [class.theme-editor-tab--active]="activeEditorTab === 'team2'" type="button" role="tab" [attr.aria-selected]="activeEditorTab === 'team2'" (click)="selectEditorTab('team2')">Team 2</button>
                    <button class="theme-editor-tab" [class.theme-editor-tab--active]="activeEditorTab === 'projector'" type="button" role="tab" [attr.aria-selected]="activeEditorTab === 'projector'" (click)="selectEditorTab('projector')">Projector</button>
                    <button class="theme-editor-tab" [class.theme-editor-tab--active]="activeEditorTab === 'roundSummary'" type="button" role="tab" [attr.aria-selected]="activeEditorTab === 'roundSummary'" (click)="selectEditorTab('roundSummary')">Round Summary</button>
                    <button class="theme-editor-tab" [class.theme-editor-tab--active]="activeEditorTab === 'endOfGameSummary'" type="button" role="tab" [attr.aria-selected]="activeEditorTab === 'endOfGameSummary'" (click)="selectEditorTab('endOfGameSummary')">EOG Summary</button>
                  </nav>

                  @if (activeEditorTab === 'control') {
                  <div class="form-grid theme-editor-panel" role="tabpanel">
                    <label class="field field--full">
                      <span>Edit Existing Theme</span>
                      <select [ngModel]="editOpenId ?? ''" (ngModelChange)="selectThemeForEdit($event)">
                        <option value="">Create New Theme</option>
                        @for (theme of themes(); track theme.id) {
                          <option [value]="theme.id">{{ theme.category }} / {{ theme.name }} ({{ theme.themeKey }})</option>
                        }
                      </select>
                    </label>

                    <label class="field"><span>Theme Key</span><input type="text" [disabled]="isEditMode" [(ngModel)]="newTheme.themeKey" /></label>
                    <label class="field"><span>Name</span><input type="text" [(ngModel)]="newTheme.name" /></label>
                    <label class="field field--full">
                      <span>Category</span>
                      <div class="category-inline-row">
                        <select [(ngModel)]="newTheme.categoryId">
                          <option [ngValue]="null">Select Category</option>
                          @for (category of categories(); track category.id) {
                            <option [ngValue]="category.id">{{ category.name }}</option>
                          }
                        </select>
                        <button class="btn btn--ghost" type="button" [disabled]="!newTheme.categoryId" (click)="startEditSelectedCategory()">Edit</button>
                        <button class="btn btn--ghost" type="button" (click)="toggleCreateCategoryInline()">
                          {{ showCreateCategoryInline ? 'Cancel New' : 'New Category' }}
                        </button>
                      </div>

                      @if (categories().length === 0) {
                        <span class="field-help">No categories available. Create one to continue.</span>
                      }

                      @if (showCreateCategoryInline) {
                        <div class="category-inline-edit">
                          <input type="text" [(ngModel)]="newCategoryName" placeholder="New category name" maxlength="100" />
                          <button class="btn btn--primary" type="button" [disabled]="isSavingCategory" (click)="createCategoryInline()">
                            {{ isSavingCategory ? 'Saving...' : 'Save New Category' }}
                          </button>
                        </div>
                      }

                      @if (showEditCategoryInline) {
                        <div class="category-inline-edit">
                          <input type="text" [(ngModel)]="editCategoryName" placeholder="Edit category name" maxlength="100" />
                          <button class="btn btn--primary" type="button" [disabled]="isSavingCategory || editingCategoryId === null" (click)="saveEditedCategoryInline()">
                            {{ isSavingCategory ? 'Saving...' : 'Save Category Name' }}
                          </button>
                          <button class="btn btn--ghost" type="button" [disabled]="isSavingCategory" (click)="cancelEditCategoryInline()">Cancel</button>
                        </div>
                      }
                    </label>
                    <label class="field field--full"><span>Description</span><input type="text" [(ngModel)]="newTheme.description" /></label>
                    <label class="field field--checkbox"><span>Enabled</span><input type="checkbox" [(ngModel)]="newTheme.isEnabled" /></label>
                    <label class="field field--checkbox"><span>System</span><input type="checkbox" [(ngModel)]="newTheme.isSystem" /></label>
                  </div>
                  }

                  @if (activeEditorTab === 'variables') {
                   <div class="theme-variable-editor theme-editor-panel" role="tabpanel">
                     <div class="slot-group-title">Theme Variables</div>
                     <p class="field-help">Values are validated before they are sent to the API. Invalid values remain visible for correction.</p>
                     <div class="theme-variable-grid">
                       @for (definition of themeVariableDefinitions; track definition.name) {
                         <label class="field">
                           <span>{{ definition.label }} <code>{{ definition.name }}</code></span>
                            <div class="theme-variable-input-row">
                              @if (definition.kind === 'color') {
                                <input
                                  class="theme-variable-color-picker-input"
                                  type="color"
                                  [value]="colorPickerValue(getThemeVariableValue(definition.name))"
                                  [attr.aria-label]="'Choose ' + definition.label + ' color'"
                                  (input)="setPickedThemeColor(definition.name, $any($event.target).value)" />
                              }
                              <input
                                type="text"
                                class="theme-variable-text-input"
                                [value]="getThemeVariableValue(definition.name)"
                                [style.background-color]="definition.kind === 'color' ? colorPickerValue(getThemeVariableValue(definition.name)) : null"
                                [style.color]="definition.kind === 'color' ? colorTextColor(getThemeVariableValue(definition.name)) : null"
                                (click)="definition.kind === 'color' ? openColorPicker($event) : null"
                                (input)="setThemeVariableValue(definition.name, $any($event.target).value)" />
                            </div>
                           @if (themeVariableErrors[definition.name]) {
                             <span class="field-error">{{ themeVariableErrors[definition.name] }}</span>
                           }
                         </label>
                       }
                     </div>
                     <div class="actions">
                       <button class="btn btn--ghost" type="button" (click)="resetThemeVariables()">Reset to named-theme defaults</button>
                     </div>
                    </div>
                  }

                   @if (activeEditorTab === 'media') {
                     <div class="slot-groups theme-editor-panel" role="tabpanel">
                    @for (group of slotGroups; track group.title) {
                      <div class="slot-group">
                        <div class="slot-group-title">{{ group.title }}</div>
                        <div class="slot-grid">
                          @for (slotKey of group.slotKeys; track slotKey) {
                            <div class="slot" (dragover)="onSlotDragOver($event)" (drop)="onSlotDrop($event, slotKey)">
                              <div class="slot-title">{{ getSlotLabel(slotKey) }}</div>
                              <div class="slot-value">
                                @if (getAssignedArtifact(slotKey); as assigned) {
                                  <span>{{ assigned.fileName }} (#{{ assigned.id }})</span>
                                } @else {
                                  <span class="slot-placeholder">Drop artifact here</span>
                                }
                              </div>
                              <div class="slot-actions">
                                <button class="btn btn--ghost" type="button" [disabled]="selectedArtifactId === null" (click)="assignSelectedArtifact(slotKey)">Use Selected</button>
                                <button class="btn btn--ghost" type="button" (click)="clearSlot(slotKey)">Clear</button>
                              </div>
                            </div>
                          }
                       </div>
                     </div>
                     }
                   </div>
                   }

                  @if (isDisplayPropertiesTab(activeEditorTab)) {
                     <div class="theme-variable-editor theme-editor-panel display-property-editor" role="tabpanel">
                       @if (activeEditorTab === 'team2') {
                         <div class="actions display-property-actions" style="justify-content: flex-end; margin-bottom: .75rem;">
                           <button class="btn btn--primary" type="button" (click)="copyTeam1DisplayPropertiesToTeam2()">Copy T1</button>
                         </div>
                       }
                       <div class="slot-group-title">{{ getDisplayScopeLabel(activeEditorTab) }} Properties</div>
                       <p class="field-help">Set only the values you want to override. Unset values keep the current display behavior. Page background image selection remains in Images.</p>
                       @for (section of getDisplayEditorSections(activeEditorTab); track section.name) {
                           @for (container of section.containers; track container.object) {
                            <section class="display-container" [attr.data-display-container]="section.name + '/' + container.object"
                              style="border: 1px solid var(--color-border); border-radius: .65rem; padding: .8rem; margin-bottom: .8rem; background: color-mix(in srgb, var(--color-surface-1) 45%, transparent);">
                              <h3 style="display: flex; align-items: center; justify-content: space-between; font-size: 1.15rem; font-weight: 700; margin: -.8rem -.8rem .8rem; padding: .45rem .8rem; border-bottom: 1px solid var(--color-border); border-radius: .65rem .65rem 0 0; background: color-mix(in srgb, var(--color-accent) 18%, var(--color-surface-1)); color: var(--color-text);">
                                <span>{{ container.label }}</span>
                                <button type="button" class="btn btn--ghost" [attr.aria-expanded]="!isDisplayContainerCollapsed(activeEditorTab, section.name, container.object)"
                                  [attr.aria-label]="(isDisplayContainerCollapsed(activeEditorTab, section.name, container.object) ? 'Expand ' : 'Collapse ') + container.label"
                                  (click)="toggleDisplayContainer(activeEditorTab, section.name, container.object)">
                                  {{ isDisplayContainerCollapsed(activeEditorTab, section.name, container.object) ? '▸' : '▾' }}
                                </button>
                              </h3>
                              @if (!isDisplayContainerCollapsed(activeEditorTab, section.name, container.object)) {
                               <div class="display-object-grid">
                                 @for (item of container.objects; track item.object) {
                                   <div class="display-object" [attr.data-display-object]="section.name + '/' + item.object">
                                     <h4 style="display: inline-block; margin: .25rem 0 .55rem; padding: .25rem .45rem; border-radius: .3rem; background: color-mix(in srgb, #60a5fa 22%, var(--color-surface-1)); color: var(--color-text);">{{ item.object === container.object ? 'Surface properties' : getDisplayObjectLabel(activeEditorTab, section.name, item.object) }}</h4>
                                        @for (state of getDisplayEditorStates(activeEditorTab, item); track state) {
                                          <div class="display-state" [attr.data-display-state]="state">
                                            @if (state !== 'values') {
                                              <div class="display-state-title" style="display: inline-block; margin: .2rem 0 .45rem; padding: .2rem .4rem; border-radius: .3rem; background: color-mix(in srgb, #60a5fa 16%, var(--color-surface-1)); color: var(--color-text);">{{ getDisplayStateLabel(state) }}</div>
                                            }
                                            <div class="theme-variable-grid">
                                              @for (field of getDisplayEditorFields(activeEditorTab, item, state); track field.path) {
                                                <label class="field display-property-field">
                                                  <span class="display-property-label" style="display: flex; align-items: center; justify-content: space-between; gap: .4rem;">
                                                    <span style="background: color-mix(in srgb, #9ca3af 35%, transparent); border-radius: .25rem; padding: .15rem .35rem;">{{ field.label }}</span>
                                                    <button type="button" class="btn btn--ghost display-property-clear" style="padding: .2rem .4rem; font-size: .65rem;" [attr.aria-label]="'Clear ' + field.label + ' to default'"
                                                      (click)="clearDisplayEditorValue(activeEditorTab, item, state, field.path)">Clear</button>
                                                  </span>
                                                  @if (field.kind === 'color') {
                                                    <div class="theme-variable-input-row">
                                                      <input class="theme-variable-color-picker-input" type="color"
                                                        [value]="getDisplayColorPickerValue(activeEditorTab, item, state, field.path)"
                                                        [attr.aria-label]="'Choose ' + field.label"
                                                        (input)="setPickedDisplayColor(activeEditorTab, item, state, field.path, $any($event.target).value)" />
                                                      <input type="text" class="theme-variable-text-input"
                                                        [value]="getDisplayEditorValue(activeEditorTab, item, state, field.path) ?? ''"
                                                        [placeholder]="hasDisplayEditorValue(activeEditorTab, item, state, field.path) ? '' : '(None)'"
                                                        [style.background-color]="hasDisplayEditorValue(activeEditorTab, item, state, field.path) ? getDisplayColorPickerValue(activeEditorTab, item, state, field.path) : null"
                                                        [style.color]="hasDisplayEditorValue(activeEditorTab, item, state, field.path) ? getDisplayColorTextColor(activeEditorTab, item, state, field.path) : null"
                                                        (dblclick)="openDisplayColorPicker($event)"
                                                        (contextmenu)="openDisplayColorPicker($event)"
                                                        (input)="setDisplayEditorValue(activeEditorTab, item, state, field.path, $any($event.target).value)" />
                                                    </div>
                                                  } @else if (field.kind === 'range') {
                                                    <div class="display-range-field">
                                                      <input type="range"
                                                        [min]="field.min ?? 0" [max]="field.max ?? 100" [step]="field.step ?? 1"
                                                        [value]="getDisplayEditorValue(activeEditorTab, item, state, field.path) ?? 100"
                                                        [attr.aria-label]="field.label + ' percent'"
                                                        (input)="setDisplayEditorValue(activeEditorTab, item, state, field.path, $any($event.target).value)" />
                                                      <output>{{ getDisplayEditorValue(activeEditorTab, item, state, field.path) ?? 100 }}%</output>
                                                    </div>
                                                  } @else if (field.kind === 'select') {
                                                    <select class="display-property-select" [style.background-color]="'var(--color-bg-end)'" [style.color]="'var(--color-text)'" [value]="getDisplayEditorValue(activeEditorTab, item, state, field.path) ?? ''"
                                                      (change)="setDisplayEditorValue(activeEditorTab, item, state, field.path, $any($event.target).value)">
                                                      <option value="" [style.background-color]="'var(--color-bg-end)'" [style.color]="'var(--color-text)'">Use current display behavior</option>
                                                      @for (option of field.options ?? []; track option) { <option [value]="option" [style.background-color]="'var(--color-bg-end)'" [style.color]="'var(--color-text)'">{{ option }}</option> }
                                                    </select>
                                                  } @else {
                                                    <input [type]="field.kind === 'number' ? 'number' : 'text'"
                                                      [min]="field.min ?? null" [max]="field.max ?? null" [step]="field.step ?? null"
                                                      [placeholder]="field.kind === 'number' ? 'Use current display behavior' : ''"
                                                      [value]="getDisplayEditorValue(activeEditorTab, item, state, field.path) ?? ''"
                                                      (input)="setDisplayEditorValue(activeEditorTab, item, state, field.path, $any($event.target).value)" />
                                                  }
                                                </label>
                                              }
                                            </div>
                                          </div>
                                        }
                                   </div>
                                 }
                               </div>
                              }
                             </section>
                           }
                       }
                       @if (displayPropertiesErrors[activeEditorTab]) { <span class="field-error">{{ displayPropertiesErrors[activeEditorTab] }}</span> }
                    </div>
                  }

                  @if (activeEditorTab === 'images') {
                     <div class="slot-groups theme-editor-panel" role="tabpanel">
                     <div class="slot-group">
                       <div class="slot-group-title">Backgrounds</div>
                       <div class="slot-grid">
                         @for (background of visualBackgroundSlots; track background.key) {
                           <div class="slot" (dragover)="onSlotDragOver($event)" (drop)="onVisualBackgroundDrop($event, background.key)">
                             <div class="slot-title">{{ background.label }}</div>
                             <div class="slot-value">
                               @if (getAssignedVisualBackground(background.key); as assigned) { <span>{{ assigned.fileName }} (#{{ assigned.id }})</span> } @else { <span class="slot-placeholder">Empty visual placeholder</span> }
                             </div>
                             <div class="slot-actions"><button class="btn btn--ghost" type="button" [disabled]="selectedArtifactId === null" (click)="assignSelectedVisualBackground(background.key)">Use Selected Image</button><button class="btn btn--ghost" type="button" (click)="clearVisualBackground(background.key)">Clear</button></div>
                           </div>
                         }
                       </div>
                     </div>
                     <div class="slot-group">
                       <div class="slot-group-title">Page Visual Blocks</div>
                       <div class="slot-grid">
                         @for (visual of visualSlots; track visual.key) {
                           <div class="slot visual-slot" (dragover)="onSlotDragOver($event)">
                             <div class="slot-title">{{ visual.label }} · {{ visual.size === 'body' ? '540 x 540 base' : visual.size === 'header-projector' ? '540 x 540' : '75 x 75' }}</div>
                             <div class="visual-layer" (drop)="onVisualLayerDrop($event, visual.key, 'image')">
                               <span class="visual-layer__label">Image</span>
                               <span>{{ getAssignedVisualArtifact(visual.key, 'image')?.fileName ?? 'Empty visual placeholder' }}</span>
                               <button class="btn btn--ghost" type="button" [disabled]="selectedArtifactId === null" (click)="assignSelectedVisualArtifact(visual.key, 'image')">Use Selected</button>
                             </div>
                             <div class="visual-layer" (drop)="onVisualLayerDrop($event, visual.key, 'svg')">
                               <span class="visual-layer__label">SVG</span>
                               <span>{{ getAssignedVisualArtifact(visual.key, 'svg')?.fileName ?? 'Empty visual placeholder' }}</span>
                               <button class="btn btn--ghost" type="button" [disabled]="selectedArtifactId === null" (click)="assignSelectedVisualArtifact(visual.key, 'svg')">Use Selected</button>
                             </div>
                             <button class="btn btn--ghost" type="button" (click)="clearVisualBlock(visual.key)">Clear Block</button>
                           </div>
                         }
                       </div>
                     </div>
                   </div>
                  }

                  <div class="theme-preview-section">
                    <div class="theme-preview-selector">
                      <div><div class="slot-group-title">GameHost Preview</div><p class="field-help">Select a GameHost view. The fixture-backed preview is isolated from live events and updates without Save.</p></div>
                      <div class="theme-preview-buttons" role="group" aria-label="GameHost preview selection">
                        <button class="btn" [class.btn--primary]="selectedPreview === 'team'" [class.btn--ghost]="selectedPreview !== 'team'" type="button" [attr.aria-pressed]="selectedPreview === 'team'" (click)="selectPreview('team')">GameHost Team1</button>
                        <button class="btn" [class.btn--primary]="selectedPreview === 'projector'" [class.btn--ghost]="selectedPreview !== 'projector'" type="button" [attr.aria-pressed]="selectedPreview === 'projector'" (click)="selectPreview('projector')">GameHost Projector</button>
                      </div>
                    </div>
                    @if (previewState(); as fixture) { <app-theme-preview [state]="fixture" [kind]="selectedPreview" [themeVariables]="newTheme.themeVariables"></app-theme-preview> } @else if (previewError) { <div class="result result--error">{{ previewError }}</div> } @else { <div class="fixture-preview__loading">Loading preview fixture...</div> }
                  </div>
                 </div>
               </section>

            @if (errorMessage) {
              <div class="result result--error">{{ errorMessage }}</div>
            }

            @if (successMessage) {
              <div class="result result--ok">{{ successMessage }}</div>
            }

           </div>
           </div>
       </section>
         }
     </div>
  `,
styles: [`.page{min-height:100vh}.error-section{margin:2rem auto;max-width:720px;padding:1rem 1.25rem;border:1px solid color-mix(in srgb,#ef4444 35%,transparent);border-radius:.8rem;background:color-mix(in srgb,#ef4444 8%,transparent)}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1200px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:.9rem;display:flex;height:56px;justify-content:center;width:56px;font-size:1.2rem}.hero-icon--violet{background:color-mix(in srgb,#8b5cf6 18%,transparent);border:1px solid color-mix(in srgb,#8b5cf6 35%,transparent);color:#a78bfa}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.page-body-inner{display:grid;gap:1rem}.builder-grid{display:grid;grid-template-columns:minmax(280px,25%) 1fr;gap:1rem;align-items:start}.card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent)}.card-hd{border-bottom:1px solid var(--color-border);padding:.8rem 1rem}.card-hd--between{display:flex;align-items:center;justify-content:space-between;gap:.5rem}.card-title{font-weight:600}.count-pill{font-size:.8rem;color:var(--color-muted)}.btn--plus{padding:.45rem .8rem;font-size:1.1rem;line-height:1}.left-filter-grid{display:grid;gap:.8rem;padding:1rem}.common-upload{padding:0 1rem .5rem}.common-upload__label{display:flex;justify-content:center;width:100%}.common-upload__label input{display:none}.common-upload__label--busy{opacity:.7;pointer-events:none}.field{display:flex;flex-direction:column;gap:.35rem}.field--full{grid-column:1 / -1}.field input,.field textarea,.field select{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.45rem .55rem}.field--checkbox{align-items:flex-start;justify-content:flex-end}.field--checkbox input{width:18px;height:18px;margin-top:.35rem}.artifact-list{padding:.5rem 1rem 1rem;display:grid;gap:.45rem;max-height:680px;overflow:auto}.artifact-row-wrap{display:grid;grid-template-columns:1fr auto;gap:.45rem;align-items:center}.artifact-row{display:grid;gap:.2rem;text-align:left;border:1px solid var(--color-border);border-radius:.55rem;padding:.55rem .65rem;background:transparent;color:var(--color-text);cursor:pointer}.artifact-row--selected{border-color:#2563eb;background:color-mix(in srgb,#2563eb 12%,transparent)}.artifact-delete-btn{padding:.5rem .7rem}.artifact-main{font-size:.86rem}.artifact-sub{font-size:.74rem;color:var(--color-muted)}.editor-wrap{padding:1rem;display:grid;gap:1rem}.form-grid{display:grid;grid-template-columns:1fr 1fr;gap:.8rem}.theme-variable-editor,.theme-preview,.theme-preview-selector{border:1px solid var(--color-border);border-radius:.6rem;padding:.75rem}.theme-variable-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:.7rem}.field-help{color:var(--color-muted);font-size:.8rem;margin:.2rem 0 .7rem}.field-error{color:#fca5a5;font-size:.76rem}.theme-variable-editor code{color:var(--color-muted);font-size:.7rem}.theme-preview{display:grid;gap:.75rem}.theme-preview-selector{display:flex;align-items:center;justify-content:space-between;gap:1rem}.theme-preview-buttons{display:flex;flex-wrap:wrap;gap:.6rem}.preview-team,.preview-projector{border:1px solid;border-radius:.55rem;padding:.8rem;display:flex;flex-wrap:wrap;align-items:center;gap:.7rem}.fixture-preview{display:grid;gap:.8rem;border:1px solid var(--color-border);border-radius:.6rem;padding:.85rem;background:color-mix(in srgb,var(--color-surface-1) 65%,transparent)}.fixture-preview__header{display:flex;justify-content:space-between;gap:.75rem;align-items:center}.fixture-preview__header span,.fixture-preview__grid span{color:var(--color-muted);font-size:.78rem}.fixture-preview__grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:.7rem}.fixture-preview__grid div{display:grid;gap:.2rem}.fixture-preview__loading{text-align:center;color:var(--color-muted);padding:1rem}.slot-groups{display:grid;gap:.9rem}.slot-group{border:1px solid var(--color-border);border-radius:.6rem;padding:.75rem}.slot-group-title{font-size:.82rem;font-weight:700;margin-bottom:.6rem;text-transform:uppercase;letter-spacing:.03em;color:var(--color-muted)}.slot-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:.6rem}.slot{border:1px dashed var(--color-border);border-radius:.5rem;padding:.55rem;display:grid;gap:.45rem}.slot-title{font-size:.76rem;color:var(--color-muted)}.slot-value{min-height:40px;display:flex;align-items:center}.slot-placeholder{color:var(--color-muted);font-style:italic}.slot-actions{display:flex;gap:.5rem;flex-wrap:wrap}.projector-placeholder{border:1px solid var(--color-border);border-radius:.65rem;padding:1rem;background:color-mix(in srgb,var(--color-panel) 65%,transparent);min-height:130px;display:grid;align-content:center;justify-items:center;gap:.5rem}.projector-placeholder__title{font-size:.82rem;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:var(--color-muted)}.projector-placeholder__body{width:100%;max-width:560px;min-height:72px;border:1px dashed var(--color-border);border-radius:.5rem;display:flex;align-items:center;justify-content:center;color:var(--color-muted);text-align:center;padding:.55rem}.actions{display:flex;gap:.6rem;align-items:center;flex-wrap:wrap}.btn{align-items:center;border:1px solid transparent;border-radius:.6rem;cursor:pointer;display:inline-flex;font-size:.78rem;font-weight:600;gap:.35rem;line-height:1;padding:.6rem .9rem;transition:all .18s ease}.btn:disabled{cursor:not-allowed;opacity:.55}.btn--primary{background:linear-gradient(135deg,#2563eb,#1d4ed8);border-color:#1d4ed8;color:#fff}.btn--ghost{background:transparent;border-color:var(--color-border);color:var(--color-text)}.btn--danger{background:linear-gradient(135deg,#dc2626,#b91c1c);border-color:#b91c1c;color:#fff}.themes-list{padding:1rem;overflow-x:auto}.themes-table{width:100%;border-collapse:collapse}.themes-table th,.themes-table td{border-bottom:1px solid var(--color-border);padding:.7rem .6rem;text-align:left;vertical-align:top}.themes-table th{font-size:.78rem;color:var(--color-muted);font-weight:700;text-transform:uppercase;letter-spacing:.03em}.empty{padding:.8rem;color:var(--color-muted)}.result{padding:.75rem;border-radius:.55rem;font-size:.9rem;display:grid;gap:.25rem}.result--ok{border:1px solid color-mix(in srgb,#10b981 40%,transparent);background:color-mix(in srgb,#10b981 10%,transparent)}.result--error{border:1px solid color-mix(in srgb,#ef4444 40%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent)}.visual-slot{min-height:170px;aspect-ratio:1 / 1;max-width:540px}.visual-layer{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:.4rem;border:1px dashed var(--color-border);border-radius:.45rem;padding:.4rem;min-height:2.5rem}.visual-layer__label{font-size:.72rem;font-weight:600;color:var(--color-muted)}.visual-layer span:nth-child(2){min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.visual-layer img,.visual-layer svg{max-width:100%;max-height:100%;object-fit:contain}@media (max-width:1024px){.builder-grid{grid-template-columns:1fr}.artifact-list{max-height:360px}.theme-preview-selector{align-items:stretch;flex-direction:column}.theme-preview-selector .field{min-width:0}}`]
})
export class ThemeBuilderPageComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly themeCatalogService = inject(GameThemeCatalogService);
  private readonly mediaService = inject(TeamMemberMediaService);
  private readonly themePreviewService = inject(ThemePreviewService);

  forbidden = false;
  isSavingCreate = false;
  isSavingEdit = false;
  isDeleting = false;
  isUploadingArtifact = false;
  deletingArtifactId: number | null = null;

  errorMessage = '';
  successMessage = '';
  themeVariableErrors: Record<string, string> = {};
  displayPropertiesErrors: Partial<Record<DisplayScopeKey, string>> = {};
  private readonly collapsedDisplayContainers = new Set<string>();
  readonly themeVariableDefinitions = THEME_VARIABLE_DEFINITIONS;

  readonly themes = signal<GameThemeDto[]>([]);
  themesSearch = '';
  readonly categories = signal<GameThemeCategoryDto[]>([]);
  readonly availableArtifacts = signal<ThemeBuilderArtifact[]>([]);
  readonly previewState = signal<ThemePreviewState | null>(null);

  readonly slotGroups = ThemeReferenceGroups;
  readonly visualBackgroundSlots = ThemeVisualBackgroundSlots;
  readonly visualSlots = ThemeVisualSlots;

  artifactSearch = '';
  artifactTypeFilter: ArtifactMediaTypeFilter = 'all';
  selectedArtifactId: number | null = null;
  selectedPreview: ThemePreviewKind = 'team';
  activeEditorTab: ThemeEditorTab = 'control';
  previewError = '';
  private readonly previewArtifactUrls = new Map<number, string>();
  private readonly previewArtifactLoads = new Map<number, Subscription>();

  isSavingCategory = false;
  showCreateCategoryInline = false;
  showEditCategoryInline = false;
  newCategoryName = '';
  editCategoryName = '';
  editingCategoryId: number | null = null;

  editOpenId: number | null = null;
  newTheme: ThemeDraft = this.createEmptyDraft(0);

  get filteredThemes(): GameThemeDto[] {
    const search = this.themesSearch.trim().toLocaleLowerCase();
    if (!search) {
      return this.themes();
    }

    return this.themes().filter(theme => [
      theme.themeKey,
      theme.name,
      theme.category,
      theme.isEnabled ? 'enabled' : 'disabled',
      theme.isSystem ? 'system' : 'custom'
    ].some(value => String(value ?? '').toLocaleLowerCase().includes(search)));
  }

  get isEditMode(): boolean {
    return this.editOpenId !== null;
  }

  selectEditorTab(tab: ThemeEditorTab): void {
    this.activeEditorTab = tab;
  }

  isDisplayPropertiesTab(tab: ThemeEditorTab): tab is DisplayScopeKey {
    return tab === 'team1' || tab === 'team2' || tab === 'projector' || tab === 'roundSummary' || tab === 'endOfGameSummary';
  }

  getDisplayScopeLabel(scope: DisplayScopeKey): string {
    return ({ team1: 'Team 1', team2: 'Team 2', projector: 'Projector', roundSummary: 'Round Summary', endOfGameSummary: 'EOG Summary' })[scope];
  }

  copyTeam1DisplayPropertiesToTeam2(): void {
    const team2Sections: DisplayPropertiesDto['team2']['sections'] = {};

    for (const [sectionName, section] of Object.entries(this.newTheme.displayProperties.team1.sections)) {
      const team2SectionName = sectionName.startsWith('Team1')
        ? sectionName.replace(/^Team1/, 'Team2')
        : sectionName;
      const team2Objects = DISPLAY_OBJECTS_BY_SCOPE.team2[team2SectionName];
      if (!team2Objects) continue;

      const objects: typeof section.objects = {};
      for (const [objectName, object] of Object.entries(section.objects)) {
        const team2ObjectName = team2Objects.includes(objectName)
          ? objectName
          : objectName.startsWith('Team1')
            ? objectName.replace(/^Team1/, 'Team2')
            : objectName;
        if (team2Objects.includes(team2ObjectName)) {
          objects[team2ObjectName] = JSON.parse(JSON.stringify(object));
        }
      }

      if (Object.keys(objects).length > 0) {
        team2Sections[team2SectionName] = { objects };
      }
    }

    this.newTheme.displayProperties = {
      ...this.newTheme.displayProperties,
      team2: { sections: team2Sections }
    };

    const error = this.validateDisplayScope('team2', this.newTheme.displayProperties.team2);
    if (error) this.displayPropertiesErrors.team2 = error;
    else delete this.displayPropertiesErrors.team2;
  }

  isDisplayContainerCollapsed(scope: DisplayScopeKey, section: string, container: string): boolean {
    return this.collapsedDisplayContainers.has(`${scope}|${section}|${container}`);
  }

  toggleDisplayContainer(scope: DisplayScopeKey, section: string, container: string): void {
    const key = `${scope}|${section}|${container}`;
    if (this.collapsedDisplayContainers.has(key)) this.collapsedDisplayContainers.delete(key);
    else this.collapsedDisplayContainers.add(key);
  }

  getDisplaySectionLabel(scope: DisplayScopeKey, section: string): string {
    if (section === 'PageSurface') return 'Main Background';
    if (scope === 'roundSummary' && section === 'ProjectorRoundSummary') return 'End-of-Round Summary';
    return DISPLAY_SECTION_LABELS[section] ?? section;
  }

  getDisplayObjectLabel(scope: DisplayScopeKey, section: string, object: string): string {
    if (object === 'Team1CurrentPlayersContent' && scope === 'projector') return 'Team 1 Current Players';
    if (object === 'Team2CurrentPlayersContent' && scope === 'projector') return 'Team 2 Current Players';
    return DISPLAY_OBJECT_LABELS[object] ?? object.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  getDisplayEditorObjectHeading(scope: DisplayScopeKey, section: string, container: DisplayEditorContainer, item: DisplayEditorObject): string {
    const label = this.getDisplayObjectLabel(scope, section, item.object);
    if (item.object === 'Surface') return 'Surface properties';
    if (item.object === container.object) return `${label} properties`;
    return label;
  }

  getDisplayEditorSections(scope: DisplayScopeKey): DisplayEditorSection[] {
    return Object.entries(DISPLAY_OBJECTS_BY_SCOPE[scope]).map(([section, objectNames]) => ({
      name: section,
      label: this.getDisplaySectionLabel(scope, section),
      objects: objectNames.map(object => ({
        section,
        object,
        baseFields: this.getDisplayBaseFields(section, object),
        supportsStates: DISPLAY_STATEFUL_OBJECTS.has(`${scope}|${section}|${object}`)
      })),
      containers: this.getDisplayContainers(scope, section, objectNames)
    }));
  }

  private getDisplayContainers(scope: DisplayScopeKey, section: string, objectNames: string[]): DisplayEditorContainer[] {
    const containers: DisplayEditorContainer[] = [];
    let activeContainer: DisplayEditorContainer | undefined;

    for (const object of objectNames) {
      const item: DisplayEditorObject = {
        section,
        object,
        baseFields: this.getDisplayBaseFields(section, object),
        supportsStates: DISPLAY_STATEFUL_OBJECTS.has(`${scope}|${section}|${object}`)
      };

      if (DISPLAY_CONTAINER_OBJECTS.has(`${scope}|${section}|${object}`)) {
        activeContainer = {
          object,
          label: object === 'Background' && section === 'PageSurface'
            ? 'Main Background'
            : object === 'Surface'
              ? this.getDisplaySectionLabel(scope, section)
              : this.getDisplayObjectLabel(scope, section, object),
          objects: [item]
        };
        containers.push(activeContainer);
      } else {
        if (!activeContainer) {
          activeContainer = { object: '__uncontained__', label: this.getDisplaySectionLabel(scope, section), objects: [] };
          containers.push(activeContainer);
        }
        activeContainer.objects.push(item);
      }
    }

    return containers;
  }

  getDisplayEditorStates(scope: DisplayScopeKey, item: DisplayEditorObject): DisplayValueState[] {
    return DISPLAY_STATEFUL_OBJECTS.has(`${scope}|${item.section}|${item.object}`)
      ? ['values', 'selected', 'unselected']
      : ['values'];
  }

  getDisplayStateLabel(state: DisplayValueState): string {
    return state === 'values' ? 'Base values' : state === 'selected' ? 'Selected' : 'Unselected';
  }

  getDisplayEditorFields(scope: DisplayScopeKey, item: DisplayEditorObject, state: DisplayValueState): DisplayEditorField[] {
    const allowed = state === 'values' ? item.baseFields : DISPLAY_CONDITIONAL_FIELDS;
    return allowed.map(path => DISPLAY_FIELD_DEFINITIONS[path]);
  }

  getDisplayEditorValue(scope: DisplayScopeKey, item: DisplayEditorObject, state: DisplayValueState, path: DisplayFieldPath): string | number | undefined {
    const values = this.getDisplayValues(scope, item, state);
    const value = this.readDisplayField(values, path);
    return value == null ? undefined : value;
  }

  hasDisplayEditorValue(scope: DisplayScopeKey, item: DisplayEditorObject, state: DisplayValueState, path: DisplayFieldPath): boolean {
    return this.getDisplayEditorValue(scope, item, state, path) !== undefined;
  }

  clearDisplayEditorValue(scope: DisplayScopeKey, item: DisplayEditorObject, state: DisplayValueState, path: DisplayFieldPath): void {
    this.setDisplayEditorValue(scope, item, state, path, '');
  }

  getDisplayColorPickerValue(scope: DisplayScopeKey, item: DisplayEditorObject, state: DisplayValueState, path: DisplayFieldPath): string {
    return colorPickerValue(String(this.getDisplayEditorValue(scope, item, state, path) ?? '#000000'));
  }

  openDisplayColorPicker(event: MouseEvent): void {
    event.preventDefault();
    const textInput = event.currentTarget as HTMLInputElement;
    const colorPicker = textInput.previousElementSibling as HTMLInputElement | null;
    if (colorPicker?.type !== 'color') return;
    if (typeof colorPicker.showPicker === 'function') colorPicker.showPicker();
    else colorPicker.click();
  }

  getDisplayColorTextColor(scope: DisplayScopeKey, item: DisplayEditorObject, state: DisplayValueState, path: DisplayFieldPath): string {
    return colorTextColor(String(this.getDisplayEditorValue(scope, item, state, path) ?? '#000000'));
  }

  setPickedDisplayColor(scope: DisplayScopeKey, item: DisplayEditorObject, state: DisplayValueState, path: DisplayFieldPath, color: string): void {
    const existing = String(this.getDisplayEditorValue(scope, item, state, path) ?? '');
    this.setDisplayEditorValue(scope, item, state, path, formatPickedColor(color, existing));
  }

  setDisplayEditorValue(scope: DisplayScopeKey, item: DisplayEditorObject, state: DisplayValueState, path: DisplayFieldPath, rawValue: string): void {
    const field = DISPLAY_FIELD_DEFINITIONS[path];
    const numericValue = (field.kind === 'number' || field.kind === 'range') && rawValue !== '' ? Number(rawValue) : undefined;
    if (numericValue !== undefined && !Number.isFinite(numericValue)) {
      this.displayPropertiesErrors[scope] = `${field.label} must be a valid number.`;
      return;
    }
    const value: string | number | undefined = rawValue === '' ? undefined : field.kind === 'number' || field.kind === 'range' ? numericValue : rawValue;
    const displayProperties = JSON.parse(JSON.stringify(this.newTheme.displayProperties)) as DisplayPropertiesDto;
    const scopeProperties = displayProperties[scope];
    const section = scopeProperties.sections[item.section] ?? { objects: {} };
    const object = section.objects[item.object] ?? { values: {} };
    const values = state === 'values' ? object.values : (object[state] ?? {});

    this.writeDisplayField(values, path, value);
    if (state === 'values') {
      object.values = values;
    } else if (this.hasDisplayValues(values)) {
      object[state] = values;
    } else {
      delete object[state];
    }

    if (!this.hasDisplayValues(object.values) && !object.selected && !object.unselected) {
      delete section.objects[item.object];
      if (Object.keys(section.objects).length === 0) delete scopeProperties.sections[item.section];
    } else {
      section.objects[item.object] = object;
      scopeProperties.sections[item.section] = section;
    }

    this.newTheme.displayProperties = displayProperties;
    const error = this.validateDisplayScope(scope, displayProperties[scope]);
    if (error) this.displayPropertiesErrors[scope] = error;
    else delete this.displayPropertiesErrors[scope];
  }

  private getDisplayBaseFields(section: string, object: string): DisplayFieldPath[] {
    const fields = DISPLAY_BASE_FIELD_OVERRIDES[`${section}|${object}`]
      ?? ['backgroundColor', 'border.color', 'border.cornerStyle', 'border.thicknessPx', 'font.color', 'font.sizeRem'];
    return object === 'Surface' ? fields.filter(field => !field.startsWith('font.')) : fields;
  }

  private getDisplayValues(scope: DisplayScopeKey, item: DisplayEditorObject, state: DisplayValueState): DisplayPropertyValuesDto {
    const itemConfig = this.newTheme.displayProperties[scope].sections[item.section]?.objects[item.object];
    return state === 'values' ? (itemConfig?.values ?? {}) : (itemConfig?.[state] ?? {});
  }

  private readDisplayField(values: DisplayPropertyValuesDto, path: DisplayFieldPath): string | number | undefined {
    if (path === 'backgroundColor' || path === 'imageOpacityPercent') return values[path] ?? undefined;
    const [group, field] = path.split('.') as ['border' | 'font' | 'svg', string];
    const groupValues = values[group] as Record<string, string | number | null | undefined> | null | undefined;
    return groupValues?.[field] ?? undefined;
  }

  private writeDisplayField(values: DisplayPropertyValuesDto, path: DisplayFieldPath, value: string | number | undefined): void {
    if (path === 'backgroundColor' || path === 'imageOpacityPercent') {
      if (value === undefined) delete values[path];
      else values[path] = value as never;
      return;
    }

    const [group, field] = path.split('.') as ['border' | 'font' | 'svg', string];
    const groupValues = { ...((values[group] ?? {}) as Record<string, string | number | null | undefined>) };
    if (value === undefined) delete groupValues[field];
    else groupValues[field] = value;
    if (Object.keys(groupValues).length === 0) delete values[group];
    else values[group] = groupValues as never;
  }

  private hasDisplayValues(values: DisplayPropertyValuesDto): boolean {
    return Object.keys(values).length > 0;
  }

  private validateDisplayScope(scopeKey: DisplayScopeKey, scope: DisplayPropertiesDto[DisplayScopeKey] | null): string | null {
    if (!scope || typeof scope !== 'object' || !scope.sections || typeof scope.sections !== 'object' || Array.isArray(scope.sections)) {
      return 'A sections object is required.';
    }
    const scopeError = this.findUnknownKeys(scope, ['sections']);
    if (scopeError) return scopeError;

    for (const [sectionName, section] of Object.entries(scope.sections)) {
      if (!sectionName.trim() || !section?.objects || typeof section.objects !== 'object' || Array.isArray(section.objects)) {
        return 'Each section requires a name and objects dictionary.';
      }
      const sectionError = this.findUnknownKeys(section, ['objects']);
      if (sectionError) return `${sectionName}: ${sectionError}`;
      for (const [objectName, object] of Object.entries(section.objects)) {
        if (!objectName.trim() || !object || typeof object !== 'object' || !object.values) {
          return 'Each object requires a name and values object.';
        }
        const objectError = this.findUnknownKeys(object, ['values', 'selected', 'unselected']);
        if (objectError) return `${sectionName}/${objectName}: ${objectError}`;
        const workbookError = this.validateWorkbookDisplayObject(scopeKey, sectionName, objectName, object);
        if (workbookError) return workbookError;
        for (const values of [object.values, object.selected, object.unselected]) {
          if (!values) continue;
          const valueError = this.validateDisplayValueKeys(values);
          if (valueError) return `${sectionName}/${objectName}: ${valueError}`;
          const validationError = this.validateDisplayValues(values);
          if (validationError) return `${sectionName}/${objectName}: ${validationError}`;
        }
      }
    }
    return null;
  }

  private validateDisplayValueKeys(values: DisplayPropertyValuesDto): string | null {
    const valuesError = this.findUnknownKeys(values, ['backgroundColor', 'imageOpacityPercent', 'border', 'font', 'svg']);
    if (valuesError) return valuesError;
    if (values.border) {
      const error = this.findUnknownKeys(values.border, ['color', 'cornerStyle', 'thicknessPx']);
      if (error) return error;
    }
    if (values.font) {
      const error = this.findUnknownKeys(values.font, ['color', 'sizeRem']);
      if (error) return error;
    }
    if (values.svg) {
      const error = this.findUnknownKeys(values.svg, ['source', 'sizePx', 'direction', 'color']);
      if (error) return error;
    }
    return null;
  }

  private findUnknownKeys(value: object, allowedKeys: string[]): string | null {
    const unknownKey = Object.keys(value).find(key => !allowedKeys.includes(key));
    return unknownKey ? `Unknown display property '${unknownKey}'.` : null;
  }

  private validateWorkbookDisplayObject(
    scopeKey: DisplayScopeKey,
    sectionName: string,
    objectName: string,
    object: DisplayPropertiesDto[DisplayScopeKey]['sections'][string]['objects'][string]
  ): string | null {
    const objectNames = DISPLAY_OBJECTS_BY_SCOPE[scopeKey]?.[sectionName];
    if (!objectNames?.includes(objectName)) return `Unsupported display object ${sectionName}/${objectName} for this scope.`;
    const objectKey = `${scopeKey}|${sectionName}|${objectName}`;
    const statefulObject = DISPLAY_STATEFUL_OBJECTS.has(objectKey);
    const baseFields = this.getDisplayBaseFields(sectionName, objectName);
    for (const [state, values] of [['values', object.values], ['selected', object.selected], ['unselected', object.unselected]] as const) {
      if (!values) continue;
      if ((state === 'selected' || state === 'unselected') && !statefulObject) return `Selected/unselected values are not supported for ${sectionName}/${objectName}.`;
      const conditional = state === 'selected' || state === 'unselected';
      const allowedFields = conditional ? DISPLAY_CONDITIONAL_FIELDS : baseFields;
      if (objectName === 'Surface') {
        const withoutFont = allowedFields.filter(field => field !== 'font.color' && field !== 'font.sizeRem');
        if (values.backgroundColor != null && !withoutFont.includes('backgroundColor')) return `backgroundColor is not supported for ${sectionName}/${objectName} in ${state}.`;
      }
      const present: [DisplayFieldPath | 'svg.rotationDegrees', boolean][] = [
        ['backgroundColor', values.backgroundColor != null], ['imageOpacityPercent', values.imageOpacityPercent != null],
        ['border.color', values.border?.color != null], ['border.cornerStyle', values.border?.cornerStyle != null],
        ['border.thicknessPx', values.border?.thicknessPx != null], ['font.color', values.font?.color != null],
        ['font.sizeRem', values.font?.sizeRem != null], ['svg.source', values.svg?.source != null],
        ['svg.sizePx', values.svg?.sizePx != null], ['svg.direction', values.svg?.direction != null],
        ['svg.color', values.svg?.color != null], ['svg.rotationDegrees', values.svg?.rotationDegrees != null]
      ];
      const effectiveFields = objectName === 'Surface' ? allowedFields.filter(field => !field.startsWith('font.')) : allowedFields;
      const effectiveFieldSet = new Set<string>(effectiveFields);
      const unsupported = present.find(([field, isSet]) => isSet && !effectiveFieldSet.has(field));
      if (unsupported) return `${unsupported[0]} is not supported for ${sectionName}/${objectName} in ${state}.`;
    }
    return null;
  }

  private validateDisplayValues(values: DisplayPropertyValuesDto): string | null {
    if (values.imageOpacityPercent != null && (values.imageOpacityPercent < 0 || values.imageOpacityPercent > 100)) return 'Image opacity must be from 0 to 100 percent.';
    if (values.border?.thicknessPx != null && values.border.thicknessPx < 0) return 'Border thickness must be non-negative.';
    if (values.font?.sizeRem != null && values.font.sizeRem <= 0) return 'Font size must be greater than zero rem.';
    if (values.svg?.sizePx != null && values.svg.sizePx <= 0) return 'SVG size must be greater than zero pixels.';
    if (values.svg?.direction != null && values.svg.direction !== 'Horizontal' && values.svg.direction !== 'Vertical') return 'SVG direction must be Horizontal or Vertical.';
    if (values.svg?.source != null && !this.isValidSvgReference(values.svg.source)) return 'SVG source must be an HTTP(S) URL or base64-encoded SVG data URI.';
    const colors = [values.backgroundColor, values.border?.color, values.font?.color, values.svg?.color].filter((color): color is string => color != null);
    if (colors.some(color => !/^(#[0-9a-f]{3,8}|rgba?\([\d.,%\s]+\)|hsla?\([\d.,%\s]+\))$/i.test(color))) return 'Colors must use hex, rgb(a), or hsl(a) notation; include alpha in the color when needed.';
    return null;
  }

  private isValidSvgReference(source: string): boolean {
    if (/^https?:\/\//i.test(source)) return true;
    const match = source.match(/^data:image\/svg\+xml;base64,([a-z0-9+/]+=*)$/i);
    if (!match) return false;
    try { return atob(match[1]).length > 0; } catch { return false; }
  }

  async ngOnInit(): Promise<void> {
    const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
    if (!isSiteAdmin) {
      this.forbidden = true;
      return;
    }

    await this.loadCategories();
    await this.loadThemes();
    await this.loadArtifacts();
    await this.loadPreviewFixture(this.selectedPreview);
  }

  ngOnDestroy(): void {
    for (const subscription of this.previewArtifactLoads.values()) {
      subscription.unsubscribe();
    }
    for (const url of this.previewArtifactUrls.values()) {
      URL.revokeObjectURL(url);
    }
  }

  selectPreview(kind: ThemePreviewKind): void {
    this.selectedPreview = kind;
    void this.loadPreviewFixture(kind);
  }

  private async loadPreviewFixture(kind: ThemePreviewKind): Promise<void> {
    this.previewError = '';
    this.previewState.set(null);

    try {
      const fixture = await firstValueFrom(this.themePreviewService.loadFixture(kind));
      this.previewState.set(this.applyPreviewTheme(fixture));
      await this.loadAssignedPreviewArtifacts();
    } catch {
      this.previewError = `Unable to load the ${kind} preview fixture.`;
    }
  }

  private applyPreviewTheme(fixture: ThemePreviewState): ThemePreviewState {
    return {
      ...fixture,
      themeVariables: { ...fixture.themeVariables, ...this.newTheme.themeVariables }
    };
  }

  private async loadAssignedPreviewArtifacts(): Promise<void> {
    const artifactIds = this.getAssignedVisualArtifactIds();
    this.releaseUnusedPreviewArtifactUrls(artifactIds);

    await Promise.all([...artifactIds].map(artifactId => this.loadPreviewArtifact(artifactId)));
    this.applyPreviewArtifactUrls();
  }

  private getAssignedVisualArtifactIds(): Set<number> {
    const artifactIds = new Set<number>();
    for (const artifactId of Object.values(this.newTheme.visualAssignments.backgrounds)) {
      if (artifactId) artifactIds.add(artifactId);
    }
    for (const block of Object.values(this.newTheme.visualAssignments.blocks)) {
      if (block.image) artifactIds.add(block.image);
      if (block.svg) artifactIds.add(block.svg);
    }

    return artifactIds;
  }

  private releaseUnusedPreviewArtifactUrls(assignedArtifactIds: Set<number>): void {
    for (const [artifactId, url] of this.previewArtifactUrls) {
      if (!assignedArtifactIds.has(artifactId)) {
        URL.revokeObjectURL(url);
        this.previewArtifactUrls.delete(artifactId);
      }
    }

    for (const [artifactId, subscription] of this.previewArtifactLoads) {
      if (!assignedArtifactIds.has(artifactId)) {
        subscription.unsubscribe();
        this.previewArtifactLoads.delete(artifactId);
      }
    }
  }

  private async loadPreviewArtifact(artifactId: number): Promise<void> {
    if (this.previewArtifactUrls.has(artifactId) || this.previewArtifactLoads.has(artifactId)) {
      return;
    }

    const subscription = this.mediaService.getMediaContent(artifactId).subscribe({
      next: blob => {
        this.previewArtifactUrls.set(artifactId, URL.createObjectURL(blob));
        this.previewArtifactLoads.delete(artifactId);
        this.applyPreviewArtifactUrls();
      },
      error: () => {
        this.previewArtifactLoads.delete(artifactId);
      }
    });
    this.previewArtifactLoads.set(artifactId, subscription);
  }

  private applyPreviewArtifactUrls(): void {
    const fixture = this.previewState();
    if (!fixture) return;

    const visuals = structuredClone(fixture.visuals);
    const backgroundUrl = (key: ThemeVisualBackgroundKey): ThemePreviewVisualReference | null => {
      const artifactId = this.newTheme.visualAssignments.backgrounds[key];
      if (!artifactId) return null;
      const artifact = this.availableArtifacts().find(item => item.id === artifactId);
      const localUrl = this.previewArtifactUrls.get(artifactId);
      return artifact && localUrl ? { artifactId, contentType: artifact.contentType, localUrl } : null;
    };
    visuals.teamBackground = backgroundUrl('team1');
    visuals.team2Background = backgroundUrl('team2');
    visuals.projectorBackground = backgroundUrl('projector');
    visuals.roundSummaryBackground = backgroundUrl('roundSummary');
    visuals.endOfGameSummaryBackground = backgroundUrl('endOfGameSummary');
    for (const blockKey of ThemeVisualBlockKeys) {
      for (const layer of ['image', 'svg'] as const) {
        const artifactId = this.newTheme.visualAssignments.blocks[blockKey][layer];
        if (!artifactId) {
          visuals.blocks[blockKey][layer] = null;
          continue;
        }
        const artifact = this.availableArtifacts().find(item => item.id === artifactId);
        const localUrl = this.previewArtifactUrls.get(artifactId);
        visuals.blocks[blockKey][layer] = artifact && localUrl
          ? { artifactId, contentType: artifact.contentType, localUrl }
          : null;
      }
    }
    this.previewState.set({ ...fixture, visuals });
  }

  async createTheme(): Promise<void> {
    this.clearMessages();
    const payload = this.mapDraftToCreate(this.newTheme);
    if (!payload) {
      this.errorMessage = Object.keys(this.themeVariableErrors).length > 0
        ? 'Correct the highlighted theme-variable errors before saving.'
        : Object.keys(this.displayPropertiesErrors).length > 0
          ? 'Correct the highlighted display-property errors before saving.'
          : 'Theme key, name, and category selection are required.';
      return;
    }

    this.isSavingCreate = true;
    try {
      await firstValueFrom(this.themeCatalogService.create(payload));
      this.successMessage = 'Theme created.';
      this.newTheme = this.createEmptyDraft(0);
      this.displayPropertiesErrors = {};
      this.editOpenId = null;
      await this.loadThemes();
    } catch {
      this.errorMessage = 'Unable to create theme.';
    } finally {
      this.isSavingCreate = false;
    }
  }

  toggleCreateCategoryInline(): void {
    this.showCreateCategoryInline = !this.showCreateCategoryInline;
    this.newCategoryName = '';
    if (this.showCreateCategoryInline) {
      this.cancelEditCategoryInline();
    }
  }

  startEditSelectedCategory(): void {
    if (!this.newTheme.categoryId) {
      return;
    }

    const category = this.categories().find(x => x.id === this.newTheme.categoryId);
    if (!category) {
      return;
    }

    this.showCreateCategoryInline = false;
    this.showEditCategoryInline = true;
    this.editingCategoryId = category.id;
    this.editCategoryName = category.name;
  }

  cancelEditCategoryInline(): void {
    this.showEditCategoryInline = false;
    this.editingCategoryId = null;
    this.editCategoryName = '';
  }

  async createCategoryInline(): Promise<void> {
    this.clearMessages();
    const name = this.newCategoryName.trim();
    if (!name) {
      this.errorMessage = 'Category name is required.';
      return;
    }

    this.isSavingCategory = true;
    try {
      const created = await firstValueFrom(this.themeCatalogService.createCategory({ name }));
      this.successMessage = 'Category created.';
      this.newCategoryName = '';
      this.showCreateCategoryInline = false;
      await this.loadCategories();
      this.newTheme.categoryId = created.id;
      await this.loadThemes();
    } catch (error) {
      this.errorMessage = this.resolveErrorMessage(error, 'Unable to create category.');
    } finally {
      this.isSavingCategory = false;
    }
  }

  async saveEditedCategoryInline(): Promise<void> {
    this.clearMessages();
    if (!this.editingCategoryId) {
      return;
    }

    const name = this.editCategoryName.trim();
    if (!name) {
      this.errorMessage = 'Category name is required.';
      return;
    }

    this.isSavingCategory = true;
    try {
      await firstValueFrom(this.themeCatalogService.updateCategory(this.editingCategoryId, { name }));
      this.successMessage = 'Category updated.';
      const keepCategoryId = this.editingCategoryId;
      this.cancelEditCategoryInline();
      await this.loadCategories();
      await this.loadThemes();
      this.newTheme.categoryId = keepCategoryId;
      if (this.editOpenId !== null) {
        this.selectThemeForEdit(this.editOpenId);
      }
    } catch (error) {
      this.errorMessage = this.resolveErrorMessage(error, 'Unable to update category.');
    } finally {
      this.isSavingCategory = false;
    }
  }

  startCreateTheme(): void {
    this.editOpenId = null;
    this.newTheme = this.createEmptyDraft(0);
    this.displayPropertiesErrors = {};
    this.showCreateCategoryInline = false;
    this.cancelEditCategoryInline();
  }

  selectThemeForEdit(rawId: number | string | null): void {
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0) {
      this.startCreateTheme();
      return;
    }

    const theme = this.themes().find(t => t.id === id);
    if (!theme) {
      return;
    }

    this.editOpenId = id;
    this.newTheme = this.mapThemeToDraft(theme);
    this.displayPropertiesErrors = {};
  }

  async saveEdit(id: number): Promise<void> {
    this.clearMessages();

    const payload = this.mapDraftToUpdate(this.newTheme);
    if (!payload) {
      this.errorMessage = Object.keys(this.themeVariableErrors).length > 0
        ? 'Correct the highlighted theme-variable errors before saving.'
        : Object.keys(this.displayPropertiesErrors).length > 0
          ? 'Correct the highlighted display-property errors before saving.'
          : 'Name and category selection are required.';
      return;
    }

    this.isSavingEdit = true;
    try {
      await firstValueFrom(this.themeCatalogService.update(id, payload));
      this.successMessage = 'Theme updated.';
      await this.loadThemes();
      this.selectThemeForEdit(id);
    } catch {
      this.errorMessage = 'Unable to update theme.';
    } finally {
      this.isSavingEdit = false;
    }
  }

  private async loadCategories(): Promise<void> {
    try {
      const categories = await firstValueFrom(this.themeCatalogService.getCategories());
      this.categories.set(categories);
    } catch {
      this.errorMessage = 'Unable to load categories.';
    }
  }

  async deleteTheme(id: number): Promise<void> {
    this.clearMessages();
    this.isDeleting = true;

    try {
      await firstValueFrom(this.themeCatalogService.delete(id));
      this.successMessage = 'Theme deleted.';
      await this.loadThemes();
      if (this.editOpenId === id) {
        this.startCreateTheme();
      }
    } catch {
      this.errorMessage = 'Unable to delete theme.';
    } finally {
      this.isDeleting = false;
    }
  }

  private async loadThemes(): Promise<void> {
    this.clearMessages();

    try {
      const themes = await firstValueFrom(this.themeCatalogService.getAll());
      this.themes.set(themes);
    } catch {
      this.errorMessage = 'Unable to load themes.';
    }
  }

  private async loadArtifacts(): Promise<void> {
    try {
      const artifacts = await firstValueFrom(this.mediaService.getCommonMedia());

      this.availableArtifacts.set(
        artifacts
          .filter(x => x.isEnabled && this.isCommonArtifact(x))
          .map(x => this.mapArtifact(x))
          .sort((a, b) => a.fileName.localeCompare(b.fileName))
      );
      await this.loadAssignedPreviewArtifacts();
    } catch {
      this.errorMessage = 'Unable to load artifacts-common media.';
    }
  }

  filteredArtifacts(): ThemeBuilderArtifact[] {
    const term = this.artifactSearch.trim().toLowerCase();
    return this.availableArtifacts().filter(artifact => {
      if (this.artifactTypeFilter !== 'all' && artifact.mediaType !== this.artifactTypeFilter) {
        return false;
      }

      if (!term) {
        return true;
      }

      return artifact.fileName.toLowerCase().includes(term)
        || artifact.contentType.toLowerCase().includes(term)
        || artifact.id.toString().includes(term);
    });
  }

  getMediaTypeLabel(mediaType: ThemeBuilderArtifact['mediaType']): string {
    return mediaType.toUpperCase();
  }

  async onCommonArtifactFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    const isPotentialThemeVisual = file.type.toLowerCase().startsWith('image/')
      || file.name.toLowerCase().endsWith('.svg');
    if (isPotentialThemeVisual) {
      const validationError = validateThemeVisualFile(file);
      if (validationError) {
        this.errorMessage = validationError;
        return;
      }
      if (file.type.toLowerCase() === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
        if (!isSafeSvgContent(await file.text())) {
          this.errorMessage = 'SVG files must contain sanitized static content.';
          return;
        }
      }
    }

    this.clearMessages();
    this.isUploadingArtifact = true;

    try {
      await firstValueFrom(this.mediaService.uploadCommonMedia(file));
      this.successMessage = 'Common artifact uploaded.';
      await this.loadArtifacts();
    } catch (error) {
      this.errorMessage = this.resolveCommonArtifactErrorMessage(error, 'Unable to upload common artifact.');
    } finally {
      this.isUploadingArtifact = false;
    }
  }

  async deleteCommonArtifact(artifactId: number): Promise<void> {
    this.clearMessages();
    this.deletingArtifactId = artifactId;

    try {
      await firstValueFrom(this.mediaService.deleteCommonMedia(artifactId));
      this.successMessage = 'Common artifact deleted.';
      if (this.selectedArtifactId === artifactId) {
        this.selectedArtifactId = null;
      }

      await this.loadArtifacts();
    } catch (error) {
      this.errorMessage = this.resolveCommonArtifactErrorMessage(error, 'Unable to delete common artifact.');
    } finally {
      this.deletingArtifactId = null;
    }
  }

  selectArtifact(artifactId: number): void {
    this.selectedArtifactId = artifactId;
  }

  onArtifactDragStart(event: DragEvent, artifact: ThemeBuilderArtifact): void {
    if (!event.dataTransfer) {
      return;
    }

    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('text/plain', artifact.id.toString());
  }

  onSlotDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onSlotDrop(event: DragEvent, slotKey: string): void {
    event.preventDefault();
    const raw = event.dataTransfer?.getData('text/plain') ?? '';
    const artifactId = Number(raw);
    if (!Number.isFinite(artifactId) || artifactId <= 0) {
      return;
    }

    this.assignArtifactToSlot(slotKey, artifactId);
  }

  assignSelectedArtifact(slotKey: string): void {
    if (this.selectedArtifactId === null) {
      return;
    }

    this.assignArtifactToSlot(slotKey, this.selectedArtifactId);
  }

  clearSlot(slotKey: string): void {
    this.newTheme.slotAssignments[slotKey] = null;
  }

  onVisualBackgroundDrop(event: DragEvent, backgroundKey: ThemeVisualBackgroundKey): void {
    event.preventDefault();
    this.assignVisualArtifactFromDrop(event, backgroundKey, 'image');
  }

  onVisualLayerDrop(event: DragEvent, blockKey: ThemeVisualBlockKey, layer: 'image' | 'svg'): void {
    event.preventDefault();
    this.assignVisualArtifactFromDrop(event, blockKey, layer);
  }

  assignSelectedVisualBackground(backgroundKey: ThemeVisualBackgroundKey): void {
    if (this.selectedArtifactId !== null) {
      this.assignVisualArtifact(backgroundKey, 'image', this.selectedArtifactId);
    }
  }

  assignSelectedVisualArtifact(blockKey: ThemeVisualBlockKey, layer: 'image' | 'svg'): void {
    if (this.selectedArtifactId !== null) {
      this.assignVisualArtifact(blockKey, layer, this.selectedArtifactId);
    }
  }

  clearVisualBackground(backgroundKey: ThemeVisualBackgroundKey): void {
    this.newTheme.visualAssignments.backgrounds[backgroundKey] = null;
  }

  clearVisualBlock(blockKey: ThemeVisualBlockKey): void {
    this.newTheme.visualAssignments.blocks[blockKey] = { image: null, svg: null };
  }

  getAssignedVisualBackground(backgroundKey: ThemeVisualBackgroundKey): ThemeBuilderArtifact | null {
    const id = this.newTheme.visualAssignments.backgrounds[backgroundKey];
    return id ? this.availableArtifacts().find(artifact => artifact.id === id) ?? null : null;
  }

  getAssignedVisualArtifact(blockKey: ThemeVisualBlockKey, layer: 'image' | 'svg'): ThemeBuilderArtifact | null {
    const id = this.newTheme.visualAssignments.blocks[blockKey][layer];
    return id ? this.availableArtifacts().find(artifact => artifact.id === id) ?? null : null;
  }

  private assignVisualArtifactFromDrop(
    event: DragEvent,
    target: ThemeVisualBackgroundKey | ThemeVisualBlockKey,
    layer: 'image' | 'svg'
  ): void {
    const artifactId = Number(event.dataTransfer?.getData('text/plain') ?? '');
    if (Number.isFinite(artifactId) && artifactId > 0) {
      this.assignVisualArtifact(target, layer, artifactId);
    }
  }

  private assignVisualArtifact(
    target: ThemeVisualBackgroundKey | ThemeVisualBlockKey,
    layer: 'image' | 'svg',
    artifactId: number
  ): void {
    const artifact = this.availableArtifacts().find(item => item.id === artifactId);
    if (!artifact || !isAllowedThemeVisualArtifact(artifact, layer)) {
      return;
    }
    if (ThemeVisualBackgroundSlots.some(background => background.key === target)) {
      this.newTheme.visualAssignments.backgrounds[target as ThemeVisualBackgroundKey] = artifactId;
      void this.loadAssignedPreviewArtifacts();
      return;
    }
    this.newTheme.visualAssignments.blocks[target as ThemeVisualBlockKey][layer] = artifactId;
    void this.loadAssignedPreviewArtifacts();
  }

  getSlotLabel(slotKey: string): string {
    return ThemeReferenceSlots.find(x => x.key === slotKey)?.label ?? slotKey;
  }

  getAssignedArtifact(slotKey: string): ThemeBuilderArtifact | null {
    const assignedArtifactId = this.newTheme.slotAssignments[slotKey];
    if (!assignedArtifactId) {
      return null;
    }

    return this.availableArtifacts().find(x => x.id === assignedArtifactId) ?? null;
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  private resolveErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      const payload = error.error as { message?: string } | string | null;

      if (payload && typeof payload === 'object' && typeof payload.message === 'string' && payload.message.trim()) {
        return payload.message;
      }

      if (typeof payload === 'string' && payload.trim()) {
        return payload;
      }
    }

    return fallback;
  }

  private resolveCommonArtifactErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      const payload = error.error as { code?: string; message?: string } | string | null;

      if (payload && typeof payload === 'object' && payload.code === 'CommonArtifactInUse') {
        return payload.message ?? 'Cannot delete this artifact because it is referenced by an active theme.';
      }

      if (payload && typeof payload === 'object' && typeof payload.message === 'string' && payload.message.trim()) {
        return payload.message;
      }

      if (typeof payload === 'string' && payload.trim()) {
        return payload;
      }
    }

    return fallback;
  }

  private mapThemeToDraft(theme: GameThemeDto): ThemeDraft {
    const slotAssignments = this.createEmptySlotAssignments();
    const visualAssignments = createEmptyThemeVisualAssignments();
    const unmanagedReferences: GameThemeArtifactReferenceUpsertDto[] = [];

    for (const reference of theme.artifactReferences) {
      const key = this.getSlotKey(reference.mediaRole, reference.sequenceOrder);
      if (key) {
        const artifact = this.availableArtifacts().find(x => x.id === reference.artifactAssetId);
        if (artifact && this.isArtifactAllowedForSlot(key, artifact)) {
          slotAssignments[key] = reference.artifactAssetId;
        }
        continue;
      }

      if (this.mapVisualReference(visualAssignments, reference)) {
        continue;
      }

      unmanagedReferences.push({
        mediaRole: reference.mediaRole,
        sequenceOrder: reference.sequenceOrder,
        artifactAssetId: reference.artifactAssetId
      });
    }

    return {
      id: theme.id,
      themeKey: theme.themeKey,
      categoryId: theme.categoryId,
      name: theme.name,
      description: theme.description ?? '',
      isEnabled: theme.isEnabled,
      isSystem: theme.isSystem,
      slotAssignments,
      visualAssignments,
      unmanagedReferences,
      themeVariables: { ...getThemeVariableDefaults(this.getThemeKey(theme.themeKey)), ...theme.themeVariables },
      displayProperties: theme.displayProperties ?? createEmptyDisplayProperties()
    };
  }

  private mapDraftToCreate(draft: ThemeDraft): GameThemeCreateDto | null {
    if (!draft.themeKey.trim() || !draft.name.trim() || !draft.categoryId || draft.categoryId <= 0) {
      return null;
    }

    if (!this.validateDraftThemeVariables(draft) || !this.validateDraftDisplayProperties(draft)) {
      return null;
    }

    return {
      themeKey: draft.themeKey.trim(),
      categoryId: draft.categoryId,
      name: draft.name.trim(),
      description: draft.description.trim() ? draft.description.trim() : undefined,
      isSystem: draft.isSystem,
      isEnabled: draft.isEnabled,
      artifactReferences: this.buildArtifactReferences(draft),
      themeVariables: { ...draft.themeVariables },
      displayProperties: draft.displayProperties
    };
  }

  private mapDraftToUpdate(draft: ThemeDraft): GameThemeUpdateDto | null {
    if (!draft.name.trim() || !draft.categoryId || draft.categoryId <= 0) {
      return null;
    }

    if (!this.validateDraftThemeVariables(draft) || !this.validateDraftDisplayProperties(draft)) {
      return null;
    }

    return {
      categoryId: draft.categoryId,
      name: draft.name.trim(),
      description: draft.description.trim() ? draft.description.trim() : undefined,
      isEnabled: draft.isEnabled,
      artifactReferences: this.buildArtifactReferences(draft),
      themeVariables: { ...draft.themeVariables },
      displayProperties: draft.displayProperties
    };
  }

  private createEmptyDraft(id: number): ThemeDraft {
    return {
      id,
      themeKey: '',
      categoryId: null,
      name: '',
      description: '',
      isEnabled: true,
      isSystem: false,
      slotAssignments: this.createEmptySlotAssignments(),
      visualAssignments: createEmptyThemeVisualAssignments(),
      unmanagedReferences: [],
      themeVariables: getThemeVariableDefaults('dark-blue'),
      displayProperties: createEmptyDisplayProperties()
    };
  }

  private getThemeKey(themeKey: string): PortalThemeKey {
    return themeKey === 'dark' || themeKey === 'gray-black' ? themeKey : 'dark-blue';
  }

  getThemeVariableValue(name: string): string {
    return this.newTheme.themeVariables[name] ?? '';
  }

  setThemeVariableValue(name: string, value: string): void {
    this.newTheme.themeVariables[name] = value;
    this.refreshPreviewThemeVariables();
    delete this.themeVariableErrors[name];
  }

  colorPickerValue(value: string): string {
    return colorPickerValue(value);
  }

  colorTextColor(value: string): string {
    return colorTextColor(value);
  }

  openColorPicker(event: MouseEvent): void {
    const row = event.currentTarget instanceof HTMLElement ? event.currentTarget.parentElement : null;
    row?.querySelector<HTMLInputElement>('.theme-variable-color-picker-input')?.click();
  }

  setPickedThemeColor(name: string, value: string): void {
    this.setThemeVariableValue(name, formatPickedColor(value, this.getThemeVariableValue(name)));
  }

  resetThemeVariables(): void {
    this.newTheme.themeVariables = getThemeVariableDefaults(this.getThemeKey(this.newTheme.themeKey));
    this.refreshPreviewThemeVariables();
    this.themeVariableErrors = {};
  }

  private refreshPreviewThemeVariables(): void {
    const current = this.previewState();
    if (current) {
      this.previewState.set({ ...current, themeVariables: { ...current.themeVariables, ...this.newTheme.themeVariables } });
    }
  }

  private validateDraftThemeVariables(draft: ThemeDraft): boolean {
    this.themeVariableErrors = validateThemeVariables(draft.themeVariables);
    return Object.keys(this.themeVariableErrors).length === 0;
  }

  private validateDraftDisplayProperties(draft: ThemeDraft): boolean {
    const errors: Partial<Record<DisplayScopeKey, string>> = {};
    for (const scope of ['team1', 'team2', 'projector', 'roundSummary', 'endOfGameSummary'] as const) {
      const error = this.displayPropertiesErrors[scope] ?? this.validateDisplayScope(scope, draft.displayProperties[scope]);
      if (error) errors[scope] = error;
    }
    this.displayPropertiesErrors = errors;
    return Object.keys(errors).length === 0;
  }

  private createEmptySlotAssignments(): Record<string, number | null> {
    const assignments: Record<string, number | null> = {};
    for (const slot of ThemeReferenceSlots) {
      assignments[slot.key] = null;
    }

    return assignments;
  }

  private getSlotKey(mediaRole: string, sequenceOrder: number): string | null {
    const normalizedRole = this.normalizeMediaRole(mediaRole);
    return ThemeReferenceSlots.find(x => this.normalizeMediaRole(x.mediaRole) === normalizedRole && x.sequenceOrder === sequenceOrder)?.key ?? null;
  }

  private assignArtifactToSlot(slotKey: string, artifactId: number): void {
    const artifact = this.availableArtifacts().find(x => x.id === artifactId);
    if (!artifact || !this.isArtifactAllowedForSlot(slotKey, artifact)) {
      return;
    }

    this.newTheme.slotAssignments[slotKey] = artifactId;
  }

  private isArtifactAllowedForSlot(slotKey: string, artifact: ThemeBuilderArtifact): boolean {
    const slot = ThemeReferenceSlots.find(x => x.key === slotKey);
    return !slot?.mediaType || slot.mediaType === artifact.mediaType;
  }

  private normalizeMediaRole(mediaRole: string): string {
    return mediaRole.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private buildArtifactReferences(draft: ThemeDraft): GameThemeArtifactReferenceUpsertDto[] {
    const references: GameThemeArtifactReferenceUpsertDto[] = [];
    for (const slot of ThemeReferenceSlots) {
      const artifactAssetId = draft.slotAssignments[slot.key];
      if (!artifactAssetId || artifactAssetId <= 0) {
        continue;
      }

      const artifact = this.availableArtifacts().find(x => x.id === artifactAssetId);
      if (artifact && !this.isArtifactAllowedForSlot(slot.key, artifact)) {
        continue;
      }

      references.push({
        mediaRole: slot.mediaRole,
        sequenceOrder: slot.sequenceOrder,
        artifactAssetId
      });
    }

    for (const background of ThemeVisualBackgroundSlots) {
      const artifactAssetId = draft.visualAssignments.backgrounds[background.key];
      if (artifactAssetId && this.isVisualArtifactAssigned(artifactAssetId, 'image')) {
        references.push({ mediaRole: this.visualBackgroundRole(background.key), sequenceOrder: 1, artifactAssetId });
      }
    }

    for (const blockKey of ThemeVisualBlockKeys) {
      const block = draft.visualAssignments.blocks[blockKey];
      for (const layer of ['image', 'svg'] as const) {
        const artifactAssetId = block[layer];
        if (artifactAssetId && this.isVisualArtifactAssigned(artifactAssetId, layer)) {
          references.push({ mediaRole: this.visualBlockRole(blockKey, layer), sequenceOrder: 1, artifactAssetId });
        }
      }
    }

    for (const unmanaged of draft.unmanagedReferences) {
      if (!references.some(x =>
        x.mediaRole === unmanaged.mediaRole
        && x.sequenceOrder === unmanaged.sequenceOrder
        && x.artifactAssetId === unmanaged.artifactAssetId)) {
        references.push(unmanaged);
      }
    }

    return references;
  }

  private mapVisualReference(assignments: ThemeVisualAssignmentsDraft, reference: GameThemeArtifactReferenceUpsertDto): boolean {
    for (const background of ThemeVisualBackgroundSlots) {
      if (reference.mediaRole === this.visualBackgroundRole(background.key) && reference.sequenceOrder === 1) {
        assignments.backgrounds[background.key] = reference.artifactAssetId;
        return true;
      }
    }
    const legacyTeamBackgroundRole = this.normalizeMediaRole(`${ThemeVisualRolePrefix}background:team`);
    if (this.normalizeMediaRole(reference.mediaRole) === legacyTeamBackgroundRole && reference.sequenceOrder === 1) {
      assignments.backgrounds.team1 ??= reference.artifactAssetId;
      assignments.backgrounds.team2 ??= reference.artifactAssetId;
      return true;
    }
    for (const blockKey of ThemeVisualBlockKeys) {
      for (const layer of ['image', 'svg'] as const) {
        if (reference.mediaRole === this.visualBlockRole(blockKey, layer) && reference.sequenceOrder === 1) {
          assignments.blocks[blockKey][layer] = reference.artifactAssetId;
          return true;
        }
      }
    }
    return false;
  }

  private isVisualArtifactAssigned(artifactId: number, layer: 'image' | 'svg'): boolean {
    const artifact = this.availableArtifacts().find(item => item.id === artifactId);
    return !!artifact && isAllowedThemeVisualArtifact(artifact, layer);
  }

  private visualBackgroundRole(key: ThemeVisualBackgroundKey): string {
    return `${ThemeVisualRolePrefix}background:${key}`;
  }

  private visualBlockRole(key: ThemeVisualBlockKey, layer: 'image' | 'svg'): string {
    return `${ThemeVisualRolePrefix}block:${key}:${layer}`;
  }

  private isCommonArtifact(artifact: TeamMemberArtifactDto): boolean {
    const blobPath = (artifact.blobPath ?? '').toLowerCase();
    return blobPath.startsWith('artifacts-common/') || blobPath.includes('/artifacts-common/');
  }

  private mapArtifact(artifact: TeamMemberArtifactDto): ThemeBuilderArtifact {
    return {
      id: artifact.id,
      fileName: artifact.fileName,
      contentType: artifact.contentType,
      blobPath: artifact.blobPath,
      artifactScope: artifact.artifactScope,
      mediaType: this.detectMediaType(artifact)
    };
  }

  private detectMediaType(artifact: TeamMemberArtifactDto): Exclude<ArtifactMediaTypeFilter, 'all'> {
    const contentType = (artifact.contentType ?? '').toLowerCase();
    const fileName = (artifact.fileName ?? '').toLowerCase();

    if (contentType.includes('svg') || fileName.endsWith('.svg')) {
      return 'svg';
    }

    if (contentType.startsWith('audio/')) {
      return 'audio';
    }

    if (contentType.startsWith('video/')) {
      return 'video';
    }

    if (contentType.startsWith('image/')) {
      return 'image';
    }

    if (fileName.endsWith('.webm') || fileName.endsWith('.mp4') || fileName.endsWith('.mov') || fileName.endsWith('.wmv')) {
      return 'video';
    }

    if (fileName.endsWith('.m4a') || fileName.endsWith('.wav') || fileName.endsWith('.mp3')) {
      return 'audio';
    }

    return 'image';
  }

}
